// ルール本体（mch-dice の sim.js の移植）。コマンドは検証してから状態を変え、描画用イベントを返す。
// 現在の手番のヒーロー（自分・敵）どちらにも同じ関数を使う（AIもプレイヤーも同じ経路）。
import type { BuildingType, ChestLoot, ItemId, Side, StatusKey, TargetRef } from '../types';
import { BAD_STATUS_KEYS, BUILDING_TYPES, CHEST_LOOTS, STATUS_KEYS } from '../types';
import { pick, randInt } from '../rng';
import { CFG, agiTicks, type SurgeTrait, type SurgeTraitMods, type TerritoryRule, type TierEffect } from './config';
import { CELLS, adjacent, dirToward, dist, neighbors, onLine, step } from './hex';
import type { Actor, Battle, Core, DefenseUnit, Zone } from './state';
import type { BattleEvent } from './events';
import { ENEMIES } from '../data/enemies';
import { FREE_JUTSU, JUTSU_BY_ID, type JutsuDef, type JutsuPower } from '../data/jutsu';

export type Ev = BattleEvent[];
export type CmdResult = { ok: true; events: BattleEvent[] } | { ok: false; error: string };
export const ok = (events: BattleEvent[]): CmdResult => ({ ok: true, events });
export const fail = (error: string): CmdResult => ({ ok: false, error });

const SIDES: Side[] = ['blue', 'red'];
/** 決着済み（関数にしているのは TS の型の絞り込みを避けるため） */
export const over = (b: Battle) => b.phase === 'over';
export const enemyOf = (side: Side): Side => (side === 'blue' ? 'red' : 'blue');
export const hasFlag = (a: Actor, f: string) => !!a.spec && a.spec.flags.includes(f);
/** お供の能力の値（無ければ 0） */
export const compOf = (a: Actor | undefined, k: string) => a?.spec?.comp?.[k] ?? 0;
/** 回数に限りのあるお供の能力を1回使う（使えたら true） */
export function useComp(a: Actor, k: string): boolean {
  const n = compOf(a, k);
  const used = a.compUsed?.[k] ?? 0;
  if (used >= n) return false;
  (a.compUsed ??= {})[k] = used + 1;
  return true;
}

// ---------- 参照 ----------

export const actorById = (b: Battle, id: number) => b.actors.find((a) => a.id === id);
export const heroOf = (b: Battle, side: Side) => b.actors.find((a) => a.kind === 'hero' && a.side === side);
export const currentActor = (b: Battle) => (b.current == null ? undefined : actorById(b, b.current));
/** マスにいるユニット（落とし穴は数えない＝見えない） */
export const unitAt = (b: Battle, cell: number) => b.actors.find((a) => a.alive && a.cell === cell && a.type !== 'otoshiana');
export const buildingsOf = (b: Battle, side: Side) => b.actors.filter((a) => a.kind === 'building' && a.alive && a.side === side);
export const attackRange = (a: Actor) => (a.spec ? a.spec.range : 1);
/** CTB の待ち。版3（v3.ctbFine）は tick×1000/(100+AGI) の四捨五入（刻みの切り上げをやめ、速さ 150・234 の崖をなくす）。版2以下は ceil(1000/(100+AGI))×tick */
export const interval = (b: Battle, a: Actor) => (rulesOf(b) >= 3 && CFG.v3.ctbFine ? Math.max(1, Math.round((CFG.tick * 1000) / (100 + Math.max(0, a.agi)))) : agiTicks(a.agi) * CFG.tick);
export const isUnitSide = (s: string): s is Side => s === 'blue' || s === 'red';

export function passable(b: Battle, cell: number, a: Actor): boolean {
  const t = b.tiles[cell];
  if (t.t === 'block' || t.t === 'core' || (t.t === 'boss' && !t.relay)) return false;
  if (t.t === 'home' && (a.kind !== 'hero' || a.side !== t.side)) return false;
  const u = unitAt(b, cell);
  return !u || u === a;
}
/** ヒーローは自分の建物を通り抜けられる（止まれない） */
export function throughable(b: Battle, cell: number, a: Actor): boolean {
  if (a.kind !== 'hero') return false;
  const u = unitAt(b, cell);
  return !!u && u.kind === 'building' && u.side === a.side;
}
export const walkable = (b: Battle, cell: number, a: Actor) => passable(b, cell, a) || throughable(b, cell, a);

// ---------- 陣地 ----------

export const paintable = (b: Battle, cell: number) => b.tiles[cell].t === 'grass' || b.tiles[cell].relay;
export function relayCount(b: Battle, side: Side): number {
  let n = 0;
  for (const t of b.tiles) if (t.relay && t.owner === side) n++;
  return n;
}

/** 陣地率（龍脈を除いた塗れるマスに対する自分の色の割合＋龍脈×5%＋loadout の加算） */
export function territoryShare(b: Battle, side: Side): number {
  let total = 0;
  let own = 0;
  let relays = 0;
  for (let i = 0; i < b.tiles.length; i++) {
    const t = b.tiles[i];
    if (t.relay) {
      if (t.owner === side) relays++;
      continue;
    }
    if (t.t !== 'grass') continue;
    total++;
    if (t.owner === side) own++;
  }
  const bonus = heroOf(b, side)?.spec?.territoryBonus ?? 0;
  return (total ? own / total : 0) + CFG.relayShare * relays + bonus;
}
export const territoryRule = (b: Battle): TerritoryRule => CFG.territoryRules[b.rule];
export function territoryTier(b: Battle, side: Side): number {
  const s = territoryShare(b, side);
  return territoryRule(b).tiers.filter((t) => s >= t.share).length;
}
/** 段階効果の合計（heal/mat/move は和、倍率は積） */
export function tierValue(b: Battle, side: Side, key: keyof TierEffect, tier = territoryTier(b, side)): number {
  const reached = territoryRule(b).tiers.slice(0, tier);
  const mult = key.endsWith('Mult') || key === 'guard';
  let v = mult ? 1 : 0;
  for (const t of reached) {
    const x = t[key];
    if (x == null) continue;
    v = mult ? v * x : v + x;
  }
  return v;
}
export const footing = (b: Battle) => territoryRule(b).footing;
export function ownsTile(b: Battle, cell: number, side: Side): boolean {
  const t = b.tiles[cell];
  return t.owner === side || ((t.t === 'home' || t.t === 'core') && t.side === side);
}
/** 自陣の中：自分の色のマスで、周り6マスのうち3マス以上が自陣（本陣・帰還マスを含む） */
export function insideTerritory(b: Battle, cell: number | null, side: Side): boolean {
  if (cell == null || b.tiles[cell]?.owner !== side) return false;
  let n = 0;
  for (const c of neighbors(cell)) if (ownsTile(b, c, side)) n++;
  return n >= CFG.territoryDepth;
}
export const onOwnTile = (b: Battle, u: Actor) => u.kind === 'hero' && isUnitSide(u.side) && insideTerritory(b, u.cell, u.side);
/** ルールの版（無い＝昔の戦い） */
export const rulesOf = (b: Battle) => b.setup.rules ?? 0;
/** 砦（章の9番目）の手番の上限が効く戦いか（版3から。防衛戦・プロローグは除く） */
export const castleTurnLimited = (b: Battle) => rulesOf(b) >= 3 && b.index === 9 && !b.defense && b.setup.stageId > 0;
/** 自分の手番の上限（超えたら時間切れ負け）。砦は版3から短い */
export const turnLimitOf = (b: Battle) => (castleTurnLimited(b) ? CFG.v3.castleTurnLimit : CFG.turnLimit);
export const overtimeLevel = (b: Battle) =>
  b.time < CFG.overtime.start ? 0 : 1 + Math.floor((b.time - CFG.overtime.start) / CFG.overtime.step);
/** ガーディアンが生きている間、その側の本陣に結界 */
export const wardOn = (b: Battle, side: Side) => b.actors.some((u) => u.kind === 'boss' && u.alive && u.side === side);

export function materialIncome(b: Battle, side: Side): number {
  let own = 0;
  for (const t of b.tiles) if (t.owner === side && !t.relay) own++;
  const M = CFG.material;
  return M.base + Math.floor(own / M.perTiles) + M.perRelay * relayCount(b, side) + tierValue(b, side, 'mat') + (heroOf(b, side)?.spec?.matIncome ?? 0);
}

// ---------- 行動順 ----------

/** 次の n 手番の予定 [{id, at}]（復活待ちを含む）。表示用 */
export function turnOrder(b: Battle, n = 6): { id: number; at: number }[] {
  const q: { id: number; at: number }[] = [];
  for (const a of b.actors) {
    if (a.kind === 'building' || (!a.alive && a.respawnAt == null)) continue;
    if (a.side === 'neutral' && !a.engaged && !a.telegraph && !b.actors.some((h) => h.kind === 'hero' && h.alive && h.cell != null && a.cell != null && adjacent(h.cell, a.cell)))
      continue;
    const iv = interval(b, a);
    let t = a.alive ? a.nextAt : (a.respawnAt as number) + iv;
    for (let k = 0; k < n; k++, t += iv) q.push({ id: a.id, at: t });
  }
  const agi = (id: number) => actorById(b, id)!.agi;
  q.sort((x, y) => x.at - y.at || agi(y.id) - agi(x.id) || b.prio[x.id] - b.prio[y.id] || x.id - y.id);
  return q.slice(0, n);
}

// ---------- 鳥居・帰還先 ----------

/** 復活の地点：自分の鳥居（最後に建てたもの）の隣の空きマス。無ければ帰還マス */
export function returnPoint(b: Battle, a: Actor): number {
  const side = a.side as Side;
  const torii = buildingsOf(b, side).filter((u) => u.type === 'torii');
  const enemyCore = b.cores[enemyOf(side)].cell;
  for (let k = torii.length - 1; k >= 0; k--) {
    const tc = torii[k].cell as number;
    const free = neighbors(tc)
      .filter((c) => b.tiles[c].t === 'grass' && passable(b, c, a))
      .sort((x, y) => dist(x, enemyCore) - dist(y, enemyCore) || x - y);
    if (free.length) return free[0];
  }
  return b.homes[side];
}

// ---------- 手番 ----------

/** 次に動くユニットまで時刻を進め、手番を始める。復活・増援もここ。戻り値は手番を得たユニット */
export function nextTurn(b: Battle, ev: Ev): Actor | null {
  for (;;) {
    if (b.defense) checkDefenseWin(b, ev);
    if (over(b)) return null;
    let best: Actor | null = null;
    let bestAt = Infinity;
    for (const a of b.actors) {
      if (a.kind === 'building') continue;
      let at: number;
      if (a.alive) at = a.nextAt;
      else if (a.respawnAt != null) at = a.respawnAt;
      else continue;
      if (
        !best ||
        at < bestAt ||
        (at === bestAt && (a.agi > best.agi || (a.agi === best.agi && (b.prio[a.id] < b.prio[best.id] || (b.prio[a.id] === b.prio[best.id] && a.id < best.id)))))
      ) {
        best = a;
        bestAt = at;
      }
    }
    if (!best) return null;
    b.time = bestAt;
    // 時刻が数でなくなった（素早さ等の値の不具合）ら、進まない時刻で回り続けないように打ち切る
    if (!(b.time <= CFG.timeLimit)) {
      finish(b, null, 'timeout', ev);
      return null;
    }
    spawnWaves(b, ev);
    if (!best.alive) {
      best.alive = true;
      best.cell = returnPoint(b, best);
      best.hp = best.hpMax;
      best.respawnAt = null;
      best.status = undefined;
      best.nextAt = b.time + interval(b, best);
      if (best.kind === 'hero' && best.spec?.ougi && !best.ougiUsed) best.ougiRevive = true;
      ev.push({ t: 'respawn', id: best.id, cell: best.cell, hp: best.hp });
      continue;
    }
    b.current = best.id;
    beginTurn(b, best, ev);
    // 手番の始めに櫓などが最後の攻め手を倒したら、そこで勝ち（手番終了を待たない）
    if (b.defense) checkDefenseWin(b, ev);
    if (over(b)) return null;
    if (b.current == null) continue; // 手番の最初に倒れた
    return best;
  }
}

export function finish(b: Battle, winner: Side | null, reason: 'core' | 'lost' | 'timeout' | 'invalid' | 'defense', ev: Ev) {
  if (over(b)) return;
  b.winner = winner;
  b.reason = reason;
  b.phase = 'over';
  b.current = null;
  ev.push({ t: 'over', winner, reason });
}

function beginTurn(b: Battle, a: Actor, ev: Ev) {
  b.turnCount++;
  b.roll = null;
  b.reach = null;
  b.budget = 0;
  b.bonus = 0;
  if (a.kind !== 'hero') {
    ev.push({ t: 'turn', id: a.id });
    zoneTurnStart(b, a, ev);
    b.phase = 'ai';
    a.defending = false;
    if (burnTick(b, a, ev)) {
      b.current = null;
      if (!over(b)) b.phase = 'idle';
    }
    return;
  }
  const side = a.side as Side;
  if (side === 'blue') {
    if (b.stats.playerTurns >= turnLimitOf(b)) {
      finish(b, null, 'timeout', ev);
      return;
    }
    b.stats.playerTurns++;
    if (b.defense) defenseTick(b, ev);
  }
  ev.push({ t: 'turn', id: a.id });
  a.turns = (a.turns ?? 0) + 1;
  a.moved = 0;
  a.defending = false;
  a.stance = undefined;
  a.builtThisTurn = 0;
  a.itemUsed = false;
  a.rollAtk = undefined;
  a.rollGuard = undefined;
  a.freeBuild = undefined;
  a.rollRole = undefined;
  a.rollMove = undefined;
  const dead = () => {
    if (a.alive && !over(b)) return false;
    b.current = null;
    if (!over(b)) b.phase = 'idle';
    return true;
  };
  // 敵本陣の迎撃
  const enemyCore = b.cores[enemyOf(side)];
  if (!b.defense && dist(a.cell as number, enemyCore.cell) <= 2) {
    ev.push({ t: 'intercept', id: a.id, side: enemyOf(side) });
    hitActor(b, a, Math.max(1, Math.round(a.hpMax * CFG.coreIntercept * (wardOn(b, enemyOf(side)) ? CFG.wardIntercept : 1))), heroOf(b, enemyOf(side)) ?? a, ev);
    if (dead()) return;
  }
  // 状態異常：鳥居のそば・帰還マスで治る → 火傷
  const foot = footing(b);
  if (a.cell === b.homes[side]) cleanse(a, ev, 'home');
  else if (toriiWard(b, a)) cleanse(a, ev, 'torii');
  zoneTurnStart(b, a, ev);
  if (burnTick(b, a, ev) && dead()) return;
  if (b.tiles[a.cell as number]?.ground === 'spring') heal(a, Math.round(a.hpMax * CFG.ground.spring.heal), ev, 'spring');
  buildingPhase(b, a, ev);
  if (dead()) return;
  const inside = onOwnTile(b, a);
  const healFrac = tierValue(b, side, 'heal') + (inside ? foot.heal ?? 0 : 0);
  if (healFrac > 0) heal(a, Math.round(a.hpMax * healFrac), ev, 'territory');
  const mats = materialIncome(b, side);
  if (mats > 0) gainMat(b, side, mats, ev, 'income');
  if (hasFlag(a, 'regen')) heal(a, Math.round(a.hpMax * CFG.flags.regen), ev, 'regen');
  if (hasFlag(a, 'sanctuary') && inside) heal(a, Math.round(a.hpMax * CFG.flags.sanctuary.heal), ev, 'sanctuary');
  if (a.cell === b.homes[side]) heal(a, Math.ceil(a.hpMax * CFG.homeHeal), ev, 'home');
  else {
    const t = buildingsOf(b, side)
      .filter((u) => u.type === 'torii' && adjacent(u.cell as number, a.cell as number))
      .reduce((m, u) => Math.max(m, u.power ?? 0), 0);
    if (t > 0) heal(a, Math.ceil(a.hpMax * t), ev, 'torii');
  }
  // お供：水辺（岩・水のマスの隣）の回復
  const mizube = compOf(a, 'mizube');
  if (mizube > 0 && a.hp < a.hpMax && neighbors(a.cell as number).some((c) => b.tiles[c]?.t === 'block')) {
    const amt = Math.round(a.hpMax * mizube);
    ev.push({ t: 'companion', id: a.id, ability: 'mizube', amount: Math.min(amt, a.hpMax - a.hp) });
    heal(a, amt, ev, 'companion');
  }
  // お供：最初の手番に妖力の撃破ポイント（comp:surge）
  const surgePts = compOf(a, 'surge');
  if (surgePts > 0 && a.turns === 1 && CFG.surge.enabled) {
    ev.push({ t: 'companion', id: a.id, ability: 'surge', amount: surgePts });
    gainSurge(b, side, surgePts, ev);
  }
  // 式神の定期の加護：every 手番ごとに重ねる（max まで）。回復は重ねた分ずつ毎回
  boonTick(b, a, ev);
  a.ougiMove = 0;
  // 式神の加勢（奥義）：最初の手番・中盤の手番・3手番ごと・復活の後・妖力が最大のまま（倒れている間に上がった）
  if (a.spec?.ougi && !a.ougiUsed) {
    if (a.turns === 1) tryOugi(b, a, 'start', ev);
    if (a.turns === CFG.flags.ougiMidTurn) tryOugi(b, a, 'mid', ev);
    if ((a.turns ?? 0) > 0 && (a.turns ?? 0) % 3 === 0) tryOugi(b, a, 'turn3', ev);
    if (a.ougiRevive) tryOugi(b, a, 'revive', ev);
    if (CFG.surge.enabled && surgeOf(b, side).stage >= surgeMax()) tryOugi(b, a, 'surge3', ev);
    if (over(b)) return;
  }
  a.ougiRevive = false;
  // 版3・敵の武将の構え：構えたマスにいる相手へ術を放つ（または相手の手番を待つ）。手番を使ったらここで終わり
  if (a.telegraph && rivalStrike(b, a, ev)) return;
  b.bonus = (inside ? CFG.tilePaintBonus : 0) + heroMove(a) + tierValue(b, side, 'move') + (a.ougiMove ?? 0);
  // 風車守：確率で移動+1
  if (hasFlag(a, 'omamori:kazaguruma') && randInt(b, 1000) < CFG.flags.kazaguruma * 1000) b.bonus += 1;
  // お供：鎌鼬の疾駆（最初の n 手番は移動+1）
  if ((a.turns ?? 0) <= compOf(a, 'dash')) {
    b.bonus += 1;
    ev.push({ t: 'companion', id: a.id, ability: 'kamaitachi', amount: 1 });
  }
  // お供：足止め（comp:slow）。相手のヒーローから n マス以内で手番を始めると移動-1
  const foe = heroOf(b, enemyOf(side));
  const slow = compOf(foe, 'slow');
  if (slow > 0 && foe!.alive && foe!.cell != null && dist(foe!.cell, a.cell as number) <= slow) {
    ev.push({ t: 'companion', id: foe!.id, ability: 'slow', amount: CFG.status.bindMove, bid: a.id });
    afflict(b, a, 'bind', CFG.flags.slowTurns, ev, { from: foe!.id });
  }
  if (hasStatus(a, 'bind')) b.bonus -= CFG.status.bindMove;
  b.phase = 'start';
}

// ---------- 状態異常 ----------

export const hasStatus = (a: Actor, key: StatusKey) => (a.status?.[key]?.turns ?? 0) > 0;

/** 自分の鳥居のそば（1マス以内）にいる＝状態異常にかからない */
export function toriiWard(b: Battle, a: Actor): boolean {
  if (a.kind !== 'hero' || a.cell == null || !isUnitSide(a.side)) return false;
  return buildingsOf(b, a.side).some((u) => u.type === 'torii' && dist(u.cell as number, a.cell as number) <= 1);
}

/** 状態異常をかける。重ねがけせず、残り手番は長い方・火傷の量は大きい方。かかったら true */
export function afflict(b: Battle, a: Actor, key: StatusKey, turns: number, ev: Ev, opt: { dmg?: number; from?: number } = {}): boolean {
  if (!a.alive || a.kind === 'building' || a.cell == null || turns <= 0) return false;
  if (toriiWard(b, a) || hasStatus(a, 'blessed')) {
    ev.push({ t: 'resist', id: a.id, key });
    return false;
  }
  const cur = a.status?.[key];
  const next = { turns: Math.max(cur?.turns ?? 0, turns), dmg: opt.dmg != null || cur?.dmg != null ? Math.max(opt.dmg ?? 0, cur?.dmg ?? 0) || undefined : undefined, from: opt.from ?? cur?.from };
  (a.status ??= {})[key] = next;
  ev.push({ t: 'status', id: a.id, key, turns: next.turns });
  return true;
}

/** 状態異常（悪い効果）を治す。良い効果は残す */
function cleanse(a: Actor, ev: Ev, why: string) {
  const keys = BAD_STATUS_KEYS.filter((k) => hasStatus(a, k));
  if (!keys.length) return;
  for (const k of keys) delete a.status![k];
  if (!Object.keys(a.status!).length) a.status = undefined;
  ev.push({ t: 'cleanse', id: a.id, keys, why });
}

/** 良い効果をかける（鳥居・清めに関係なく。残り手番は長い方） */
function buff(a: Actor, key: StatusKey, turns: number, ev: Ev) {
  const t = Math.max(a.status?.[key]?.turns ?? 0, turns);
  (a.status ??= {})[key] = { turns: t };
  ev.push({ t: 'status', id: a.id, key, turns: t });
}

/** 手番の終わりに残り手番を1減らす */
function statusTick(a: Actor, ev: Ev) {
  if (!a.status) return;
  for (const k of STATUS_KEYS) {
    const s = a.status[k];
    if (!s) continue;
    s.turns--;
    if (s.turns <= 0) delete a.status[k];
    ev.push({ t: 'status', id: a.id, key: k, turns: Math.max(0, s.turns) });
  }
  if (!Object.keys(a.status).length) a.status = undefined;
}

/** 火傷・毒のダメージ（手番の最初）。倒れたら true */
function burnTick(b: Battle, a: Actor, ev: Ev): boolean {
  return fireTick(b, a, ev) || poisonTick(b, a, ev);
}
function poisonTick(b: Battle, a: Actor, ev: Ev): boolean {
  const s = a.status?.poison;
  if (!s || s.turns <= 0 || a.cell == null || !a.alive) return false;
  const dmg = Math.max(1, s.dmg ?? Math.round(a.hpMax * CFG.ground.miasma.dmg));
  const from = (s.from != null && actorById(b, s.from)) || a;
  ev.push({ t: 'poison', id: a.id, cell: a.cell });
  hitActor(b, a, dmg, from, ev);
  return !a.alive;
}
function fireTick(b: Battle, a: Actor, ev: Ev): boolean {
  if (a.burnNext) {
    // 古い保存データ：次の手番の燃焼を火傷に変える
    (a.status ??= {}).burn = { turns: 1, dmg: a.burnNext, from: a.id };
    a.burnNext = 0;
  }
  const s = a.status?.burn;
  if (!s || s.turns <= 0 || a.cell == null || !a.alive) return false;
  const dmg = Math.max(1, s.dmg ?? Math.round(a.hpMax * CFG.status.burnDmg));
  const from = (s.from != null && actorById(b, s.from)) || a;
  ev.push({ t: 'burn', id: a.id, cell: a.cell });
  hitActor(b, a, dmg, from, ev);
  return !a.alive;
}

/** 宝箱の中身：種とマスから決める（戦闘の乱数の流れを変えないように） */
export function chestLootAt(b: Battle, cell: number): ChestLoot {
  let h = (b.setup.seed ^ Math.imul(cell + 1, 2654435761)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39) >>> 0;
  const u = ((h ^ (h >>> 15)) >>> 0) / 4294967296;
  const w = CFG.chestLoot;
  const total = CHEST_LOOTS.reduce((s, k) => s + (w[k] ?? 0), 0);
  let acc = 0;
  for (const k of CHEST_LOOTS) {
    acc += (w[k] ?? 0) / total;
    if (u < acc) return k;
  }
  return 'zeni';
}

export function gainMat(b: Battle, side: Side, amount: number, ev: Ev, why: string) {
  const before = b.mat[side];
  b.mat[side] = Math.min(CFG.material.cap, before + amount);
  const got = b.mat[side] - before;
  if (got > 0) ev.push({ t: 'mat', side, amount: got, total: b.mat[side], why });
}

function heal(a: Actor, amount: number, ev: Ev, why: string): number {
  if (hasStatus(a, 'curse')) amount = Math.floor(amount * CFG.status.curseHeal);
  const got = Math.min(amount, a.hpMax - a.hp);
  if (got <= 0) return 0;
  a.hp += got;
  ev.push({ t: 'heal', id: a.id, amount: got, why });
  return got;
}

function endTurn(b: Battle, a: Actor, ev: Ev, keepCharge = false) {
  if (a.kind === 'hero' && !keepCharge) {
    // 版3・敵の武将の構え：放たなかった構えは解け、術の待ちが明けていて射程に相手の武将がいれば構え直す
    if (a.telegraph) a.telegraph = null;
    if (a.alive && !over(b)) rivalChargeAtEnd(b, a, ev);
  }
  a.nextAt = b.time + interval(b, a);
  a.dashing = false;
  if (a.alive) statusTick(a, ev);
  if (!over(b)) b.phase = 'idle';
  b.current = null;
  b.roll = null;
  b.reach = null;
  ev.push({ t: 'end', id: a.id });
}

// ---------- 移動 ----------

export const stepCost = (b: Battle, a: Actor, cell: number) =>
  isUnitSide(a.side) && b.tiles[cell].owner === enemyOf(a.side) ? footing(b).enemyCost || 1 : 1;

export interface ReachNode {
  d: number;
  prev: number | null;
  gain: number;
  stop: boolean;
}

/** 予算内で届くマス。同じ費用なら塗るマスが多い経路を選ぶ */
export function computeReach(b: Battle, a: Actor, budget: number): Map<number, ReachNode> {
  const start = a.cell as number;
  const out = new Map<number, ReachNode>([[start, { d: 0, prev: null, gain: 0, stop: true }]]);
  const open = [start];
  while (open.length) {
    let bi = 0;
    for (let k = 1; k < open.length; k++) if (out.get(open[k])!.d < out.get(open[bi])!.d) bi = k;
    const c = open.splice(bi, 1)[0];
    const cur = out.get(c)!;
    for (const n of neighbors(c)) {
      if (!walkable(b, n, a)) continue;
      const d = cur.d + stepCost(b, a, n);
      if (d > budget) continue;
      const t = b.tiles[n];
      const gain = cur.gain + (paintable(b, n) && t.owner !== a.side ? (t.owner ? 1.2 : 1) : 0);
      const old = out.get(n);
      if (old && (old.d < d || (old.d === d && old.gain >= gain))) continue;
      out.set(n, { d, prev: c, gain, stop: passable(b, n, a) });
      if (!open.includes(n)) open.push(n);
    }
  }
  return out;
}

/**
 * 目的地までの経路（rules 1 から）：予算の範囲で、できるだけ多く塗れる回り道（同じマスは通らない）。
 * 見えている罠（相手の陣地の狐火灯籠の隣）と、陣地ルールで状態異常になる相手の陣地へは、回り道では入らない。
 * 最短の経路より多く塗れなければ最短の経路。出発点からの全経路を1回だけ数え、行き先ごとの最良を覚える（同じ出目の間は使い回す）
 */
const ROUTE_NODES = 40000;
const routeCache = new WeakMap<object, { key: string; best: Map<number, number[]> }>();
export function paintRoute(b: Battle, a: Actor, target: number): number[] {
  const reach = reachMap(b);
  if (!reach.get(target)?.stop) return [];
  const short = pathFrom(reach, target);
  if (!(b.setup.rules ?? 0) || !b.reach) return short;
  // 出目のあと（移動の前）に術・建築で盤面が変わることがあるので、陣地と駒の位置ごとに覚える
  const key = `${a.id}:${a.cell}:${b.budget}:${b.tiles.map((t) => t.owner?.[0] ?? '-').join('')}:${b.actors.map((u) => (u.alive ? u.cell : '')).join(',')}`;
  let hit = routeCache.get(b.reach);
  if (!hit || hit.key !== key) routeCache.set(b.reach, (hit = { key, best: paintRoutes(b, a) }));
  const r = hit.best.get(target);
  return r && routeGain(b, a, r) > routeGain(b, a, short) ? r.slice() : short;
}
/** 経路で塗れる量（相手の陣地は1.2・龍脈はさらに1。整数で比べる） */
function routeGain(b: Battle, a: Actor, path: number[]): number {
  let g = 0;
  for (const c of path.slice(1)) {
    const t = b.tiles[c];
    if (!paintable(b, c) || t.owner === a.side) continue;
    g += t.owner ? 12 : 10;
    if (t.relay) g += 10;
  }
  return g;
}
function paintRoutes(b: Battle, a: Actor): Map<number, number[]> {
  const side = a.side as Side;
  const foe = enemyOf(side);
  const lanterns = b.actors.filter((u) => u.alive && u.type === 'kitsunebi' && u.side !== side && u.cell != null);
  const afflict = !!footing(b).afflict;
  const avoid = (c: number) =>
    (b.tiles[c].owner === foe && (afflict || lanterns.some((l) => adjacent(c, l.cell as number) && b.tiles[c].owner === l.side)));
  const out = new Map<number, { g: number; d: number; path: number[] }>();
  const path = [a.cell as number];
  const on = new Set(path);
  let nodes = 0;
  const walk = (c: number, d: number, g: number) => {
    if (++nodes > ROUTE_NODES) return;
    if (path.length > 1 && passable(b, c, a)) {
      const o = out.get(c);
      if (!o || g > o.g || (g === o.g && d < o.d)) out.set(c, { g, d, path: path.slice() });
    }
    for (const n of neighbors(c)) {
      if (on.has(n) || !walkable(b, n, a) || avoid(n)) continue;
      const nd = d + stepCost(b, a, n);
      if (nd > b.budget) continue;
      const t = b.tiles[n];
      const ng = g + (paintable(b, n) && t.owner !== side ? (t.owner ? 12 : 10) + (t.relay ? 10 : 0) : 0);
      path.push(n);
      on.add(n);
      walk(n, nd, ng);
      path.pop();
      on.delete(n);
    }
  };
  walk(a.cell as number, 0, 0);
  return new Map([...out].map(([c, v]) => [c, v.path]));
}

export function reachMap(b: Battle): Map<number, ReachNode> {
  return new Map((b.reach ?? []).map(([cell, d, prev, stop]) => [cell, { d, prev: prev < 0 ? null : prev, gain: 0, stop: stop !== 0 }]));
}

export function pathFrom(reach: Map<number, ReachNode>, cell: number): number[] {
  const path: number[] = [];
  for (let c: number | null = cell; c != null; c = reach.get(c)?.prev ?? null) path.unshift(c);
  return path;
}

/** なぞった経路：自分から始まり、隣を辿り、同じマスを通らず、予算内（partial=終点で止まれなくてもよい） */
export function validPath(b: Battle, a: Actor, path: unknown, partial = false): boolean {
  if (!Array.isArray(path) || path.length === 0 || path[0] !== a.cell) return false;
  if (!path.every((c) => Number.isInteger(c) && c >= 0 && c < CELLS.length)) return false;
  if (new Set(path).size !== path.length) return false;
  let cost = 0;
  for (let k = 1; k < path.length; k++) {
    if (!adjacent(path[k - 1], path[k]) || !walkable(b, path[k], a)) return false;
    cost += stepCost(b, a, path[k]);
  }
  return cost <= b.budget && (partial || path.length === 1 || passable(b, path[path.length - 1], a));
}

const canAct = (b: Battle) => b.phase === 'start' || b.phase === 'rolled' || b.phase === 'moved';
const heroTurn = (b: Battle) => {
  const a = currentActor(b);
  return a && a.kind === 'hero' && a.alive ? a : undefined;
};

/** 賽3つの役（無ければ null） */
export function diceRole(d: number[]): string | null {
  const [x, y, z] = [...d].sort((p, q) => p - q);
  if (x === z) return x === 1 ? 'pinzoro' : 'zorome';
  if (y === x + 1 && z === y + 1) return x === 4 ? 'shigoro' : 'jun';
  return null;
}
/** 賽3つの合計 → 移動（1〜4） */
export const diceMove = (sum: number) => 1 + CFG.dice3.moveAt.filter((t) => sum > t).length;
const pct = (v: number) => `${Math.round(v * 100)}%`;
/** 賽3つ（rules 2）：3つの目と合計で、移動・役・賽の条件の効果を決める */
function rollThree(b: Battle, a: Actor): CmdResult {
  const d = [1 + randInt(b, 6), 1 + randInt(b, 6), 1 + randInt(b, 6)];
  const sum = d[0] + d[1] + d[2];
  const side = a.side as Side;
  const mods = a.spec!.dice ?? {};
  const role = diceRole(d);
  const def = role ? CFG.dice3.roles[role] : null;
  a.rollRole = role ?? undefined;
  const perks: string[] = [];
  const ev: Ev = [];
  let atk = 0;
  if (def?.atk) (atk += def.atk), perks.push(`与ダメ+${pct(def.atk)}`);
  if (def?.free) (a.freeBuild = true), perks.push('建築の陣材0');
  const add = (v: number | undefined, label: string) => {
    if (!v) return;
    atk += v;
    perks.push(`${label}：与ダメ+${pct(v)}`);
  };
  if (sum % 2 === 0) add(mods.even, '丁');
  if (sum >= CFG.dice3.high) add(mods.high, '大目');
  if (new Set(d).size < 3) add(mods.pair, '揃い目');
  if (role) add(mods.role, '役');
  // 博徒の外れ：大目に届かない・役が出ない手番は与ダメが下がる
  if (sum < CFG.dice3.high && mods.lowAtk) (atk += mods.lowAtk), perks.push(`大目に届かず：与ダメ${pct(mods.lowAtk)}`);
  if (!role && mods.noRole) (atk += mods.noRole), perks.push(`役なし：与ダメ${pct(mods.noRole)}`);
  a.rollAtk = atk ? Math.max(0.3, 1 + atk) : undefined;
  if (sum % 2 === 1 && mods.odd) (a.rollGuard = 1 - mods.odd), perks.push(mods.odd > 0 ? `半：被ダメ-${pct(mods.odd)}` : `半：被ダメ+${pct(-mods.odd)}`);
  let bonus = b.bonus;
  if (sum <= CFG.dice3.low && mods.low) (bonus += mods.low), (a.rollMove = mods.low), perks.push(`小目：移動+${mods.low}`);
  const value = Math.max(a.spec!.diceMin, diceMove(sum));
  b.roll = value;
  b.budget = Math.max(1, value + bonus);
  const reach = computeReach(b, a, b.budget);
  b.reach = [...reach].map(([cell, v]) => [cell, v.d, v.prev ?? -1, v.stop ? 1 : 0]);
  b.phase = 'rolled';
  ev.push({ t: 'roll', id: a.id, value, bonus, faces: [1, 2, 3, 4], dice: d, ...(role ? { role } : {}), ...(perks.length ? { perks } : {}) });
  if (role && mods.roleMat) gainMat(b, side, Math.round(mods.roleMat), ev, 'dice');
  return ok(ev);
}
/** 術の賽（rules 2）：術・奥義を放つときに3つ振る。合計で威力が上がり、ゾロ目なら大きく上がる */
function skillDice(b: Battle, a: Actor, ev: Ev) {
  if ((b.setup.rules ?? 0) < 2 || a.kind !== 'hero') return;
  const d = [1 + randInt(b, 6), 1 + randInt(b, 6), 1 + randInt(b, 6)];
  const sum = d[0] + d[1] + d[2];
  const k = CFG.dice3.skill;
  const zorome = d[0] === d[1] && d[1] === d[2];
  const mult = Math.round((1 + ((sum - 3) / 15) * k.span * (1 + (a.spec!.dice?.skill ?? 0))) * (zorome ? k.zorome : 1) * 1000) / 1000;
  a.skillDice = mult;
  ev.push({ t: 'skillDice', id: a.id, dice: d, mult, zorome });
}

export function cmdRoll(b: Battle): CmdResult {
  const a = heroTurn(b);
  if (!a || b.phase !== 'start') return fail('phase');
  if ((b.setup.rules ?? 0) >= 2) return rollThree(b, a);
  const faces = CFG.dice;
  const value = Math.max(a.spec!.diceMin, pick(b, faces));
  b.roll = value;
  b.budget = Math.max(1, value + b.bonus);
  const reach = computeReach(b, a, b.budget);
  b.reach = [...reach].map(([cell, v]) => [cell, v.d, v.prev ?? -1, v.stop ? 1 : 0]);
  b.phase = 'rolled';
  return ok([{ t: 'roll', id: a.id, value, bonus: b.bonus, faces: faces.map((f) => Math.max(a.spec!.diceMin, f)) }]);
}

export function cmdMove(b: Battle, target: number, route?: number[]): CmdResult {
  const a = heroTurn(b);
  if (!a || b.phase !== 'rolled') return fail('phase');
  const side = a.side as Side;
  const reach = reachMap(b);
  if (!reach.get(target)?.stop) return fail('unreachable');
  if (route !== undefined && (route[route.length - 1] !== target || !validPath(b, a, route))) return fail('route');
  let path = route ? route.slice() : paintRoute(b, a, target);
  const ev: Ev = [];
  // 敵の落とし穴を通ると、そのマスで止まる（お供の見破り comp:trap があれば止まらず、通った罠を壊す）
  const sight = compOf(a, 'trap') > 0;
  const trapAt = sight ? -1 : path.slice(1).findIndex((c) => b.actors.some((u) => u.alive && u.type === 'otoshiana' && u.side !== side && u.cell === c));
  let trap: Actor | undefined;
  if (sight) {
    for (const c of path.slice(1)) {
      const t = b.actors.find((u) => u.alive && u.type === 'otoshiana' && u.side !== side && u.cell === c);
      if (!t) continue;
      t.alive = false;
      t.hp = 0;
      t.cell = null;
      ev.push({ t: 'companion', id: a.id, ability: 'hitotsume', bid: t.id });
      ev.push({ t: 'death', id: t.id, cell: c });
    }
  }
  if (trapAt >= 0) {
    path = path.slice(0, trapAt + 2);
    trap = b.actors.find((u) => u.alive && u.type === 'otoshiana' && u.cell === path[path.length - 1]);
  }
  const cell = path[path.length - 1];
  // 狐火・陣地ルールの状態異常：塗る前の持ち主で判定
  const burns = lanternHits(b, side, path.slice(1));
  const invaded = path.slice(1).some((c) => b.tiles[c].owner === enemyOf(side));
  a.cell = cell;
  const painted: number[] = [];
  const tiersBefore = { blue: territoryTier(b, 'blue'), red: territoryTier(b, 'red') };
  const capture: number[] = [];
  for (const c of path.slice(1)) {
    const t = b.tiles[c];
    if (paintable(b, c) && t.owner !== side) {
      t.owner = side;
      painted.push(c);
      if (t.relay) capture.push(c);
    }
  }
  a.moved = path.length - 1;
  if (side === 'blue') b.stats.tiles += painted.length;
  ev.push({ t: 'move', id: a.id, path, painted });
  groundStep(b, a, path.slice(1), ev);
  for (const c of capture) ev.push({ t: 'capture', id: a.id, cell: c, side });
  for (const _ of capture) relayHeal(a, ev);
  for (const s of SIDES) {
    const tier = territoryTier(b, s);
    if (tier !== tiersBefore[s]) ev.push({ t: 'territory', side: s, tier, prev: tiersBefore[s] });
  }
  const tile = b.tiles[cell];
  if (tile.pickup === 'chest') {
    tile.pickup = null;
    gainMat(b, side, CFG.chestMat, ev, 'chest');
    // 自分が開けた宝箱：中身を1つ（戦闘の乱数は使わない。種とマスで決まる）
    if (side === 'blue') {
      const loot = chestLootAt(b, cell);
      (b.stats.chests ??= []).push(loot);
      ev.push({ t: 'chest', id: a.id, cell, loot });
    }
  } else if (tile.pickup === 'shrine') {
    tile.pickup = null;
    heal(a, Math.round(a.hpMax * CFG.shrineHeal), ev, 'shrine');
  }
  b.phase = 'moved';
  // 試作：城に近づくと番人が目を覚ます
  if (CFG.guardianRouse > 0 && CFG.guardianRouseNear > 0 && !b.defense && dist(cell, b.cores[enemyOf(side)].cell) <= CFG.guardianRouseNear)
    for (const g of b.actors) if (g.kind === 'boss' && g.alive && g.side === enemyOf(side) && g.roused == null) (g.roused = a.id), (g.engaged = true);
  // 陣地ルール：相手の陣地に踏み込むと状態異常（踏み込んだ手番も1手番と数える）
  const aff = footing(b).afflict;
  if (aff && invaded) afflict(b, a, aff.key, aff.turns, ev, { from: heroOf(b, enemyOf(side))?.id });
  for (const { lantern, cell: at } of burns) {
    if (!a.alive || !lantern.alive) break;
    ev.push({ t: 'kitsunebi', id: a.id, cell: at, bid: lantern.id });
    hitActor(b, a, lantern.power ?? 1, lantern, ev);
  }
  if (trap && a.alive && !over(b)) {
    trap.alive = false;
    trap.cell = null;
    ev.push({ t: 'trap', id: a.id, cell, bid: trap.id });
    hitActor(b, a, trap.power ?? 1, trap, ev);
  }
  if ((!a.alive || over(b)) && b.current === a.id) endTurn(b, a, ev);
  else if (a.dashing && b.current === a.id) endTurn(b, a, ev); // 一撃離脱の移動はそこで手番が終わる
  return ok(ev);
}

/** 経路で入るマスのうち、相手の狐火灯籠に隣接する相手の陣地マス（灯籠ごとに1回） */
function lanternHits(b: Battle, moverSide: Side | 'neutral', cells: number[]) {
  const out: { lantern: Actor; cell: number }[] = [];
  const lanterns = b.actors.filter((u) => u.alive && u.type === 'kitsunebi' && u.side !== moverSide);
  if (!lanterns.length) return out;
  for (const l of lanterns) {
    const c = cells.find((x) => adjacent(x, l.cell as number) && b.tiles[x].owner === l.side);
    if (c !== undefined) out.push({ lantern: l, cell: c });
  }
  return out;
}

// ---------- 攻撃 ----------

export interface CoreTarget extends Core {
  kind: 'core';
  side: Side;
}
export type Target = Actor | CoreTarget;

/** line=true なら6方向の直線の上だけ */
export function targetsFrom(b: Battle, a: Actor, cell: number, range: number, line = false): TargetRef[] {
  const out: TargetRef[] = [];
  const inReach = (c: number) => dist(cell, c) <= range && (!line || onLine(cell, c));
  for (const u of b.actors) {
    if (!u.alive || u === a || u.side === a.side || u.type === 'otoshiana' || u.cell == null) continue;
    if (inReach(u.cell)) out.push({ kind: 'actor', id: u.id });
  }
  if (isUnitSide(a.side) && !(b.defense && a.side === 'blue')) {
    const core = b.cores[enemyOf(a.side)];
    if (inReach(core.cell)) out.push({ kind: 'core', side: enemyOf(a.side) });
  }
  return out;
}
/** cell から通常攻撃（skill=true なら術）で狙える相手 */
export const attackTargetsAt = (b: Battle, a: Actor, cell: number, skill: boolean): TargetRef[] =>
  // 自分を中心に放つ術（cast）を持っているときは、相手を狙う術・奥義は無い
  skill ? (a.spec?.skill.cast ? [] : targetsFrom(b, a, cell, skillRange(a), a.spec!.skill.line)) : targetsFrom(b, a, cell, attackRange(a));

/** 術が使える（待ちが明けていて、封印されていない） */
export const skillReady = (a: Actor) => (a.turns ?? 0) >= (a.skillReady ?? 0) && !hasStatus(a, 'seal');

export function resolveTarget(b: Battle, ref: TargetRef): Target | undefined {
  if (ref.kind === 'core') return b.cores[ref.side] ? { kind: 'core', side: ref.side, ...b.cores[ref.side] } : undefined;
  return actorById(b, ref.id);
}

function bannerMult(b: Battle, att: Actor): number {
  let m = 1;
  for (const u of buildingsOf(b, att.side as Side)) if (u.type === 'nobori' && dist(u.cell as number, att.cell as number) <= 1) m = Math.max(m, 1 + (u.power ?? 0));
  return m;
}

/** 土地ギミック：泥濘・毒沼を通ったら足止め・毒（通ったマスのうち1回ずつ） */
function groundStep(b: Battle, a: Actor, cells: number[], ev: Ev) {
  const g = new Set(cells.map((c) => b.tiles[c]?.ground));
  if (g.has('mire')) afflict(b, a, 'bind', CFG.ground.mire.bindTurns, ev);
  if (g.has('miasma')) afflict(b, a, 'poison', CFG.ground.miasma.turns, ev, { dmg: Math.max(1, Math.round(a.hpMax * CFG.ground.miasma.dmg)) });
}

/** ダメージ計算。予測（forecast）と実際の攻撃で同じ値 */
export function damageCalc(b: Battle, att: Actor, target: Target, useSkill = false, mult = 1): number {
  let m = mult;
  // 土地ギミック：神域に立っていると与ダメ上昇
  if (att.cell != null && b.tiles[att.cell]?.ground === 'sacred') m *= 1 + CFG.ground.sacred.dealt;
  const spec = att.kind === 'hero' ? att.spec! : null;
  const sk = useSkill && spec ? spec.skill : null;
  // 特性の術の威力は本陣には乗らない（妖力の攻撃の上乗せと同じ）
  if (sk) m *= sk.mult * (target.kind === 'core' ? 1 : 1 + (traitMods(att).skillPower ?? 0));
  if (sk && target.kind === 'building') m *= sk.buildingMult;
  // 妖力の奥義：術の威力 × mult（キャラによって本陣へさらに）
  if (sk && att.ougiCasting) m *= CFG.surge.ougi.mult * (target.kind === 'core' ? CFG.surge.ougi.coreMult * (ougiOf(att)?.core ?? 1) : 1);
  const magic = !!spec && (sk ? sk.magic || spec.magic : spec.magic);
  let def: number;
  let after = 1;
  const attSide = att.side as Side;
  if (target.kind === 'core') {
    def = target.def;
    if (wardOn(b, target.side)) after *= CFG.ward;
    if (spec) after *= CFG.coreAssault[territoryTier(b, attSide)];
    after *= 1 + CFG.overtime.add * overtimeLevel(b);
    if (spec) {
      // 妖力の攻撃の上乗せは本陣には core の分だけ（中立を倒して本陣を割る速さが上がりすぎないように）
      if (CFG.surge.enabled && isUnitSide(att.side)) {
        const e = surgeEffect(surgeOf(b, att.side).stage);
        m *= (1 + (e.core ?? 0) + (traitMods(att).core ?? 0)) / (1 + e.atk);
      }
      // 結界のある間は攻城系の上乗せ（1を超えた分）を wardSiege だけ残す
      const keep = wardOn(b, target.side) ? CFG.wardSiege : 1;
      const fs = [hasFlag(att, 'siege') ? CFG.flags.siege : 1, b.siege[attSide] > 0 ? CFG.siege.mult : 1, spec.mult.core, sk ? sk.coreMult : 1].map((f) => (f > 1 ? 1 + (f - 1) * keep : f));
      m *= CFG.coreAdditive ? 1 + fs.reduce((x, f) => x + f - 1, 0) : fs.reduce((x, f) => x * f, 1);
    }
  } else {
    def = target.def;
    if (target.kind === 'hero' && hasFlag(target, 'sanctuary') && onOwnTile(b, target)) def += Math.round(target.def * CFG.flags.sanctuary.def);
    if (spec && (target.kind === 'neutral' || target.kind === 'boss')) {
      if (hasFlag(att, 'predator')) m *= CFG.flags.predator;
      m *= target.kind === 'boss' ? spec.mult.boss : spec.mult.neutral;
      m *= tierValue(b, attSide, 'huntMult');
    }
  }
  if (spec) {
    const inside = onOwnTile(b, att);
    if (hasFlag(att, 'conqueror') && inside) m *= CFG.flags.conqueror;
    if (hasFlag(att, 'momentum') && (att.moved ?? 0) >= 2) m *= CFG.flags.momentum;
    if (hasFlag(att, 'stride')) m *= 1 + Math.min(CFG.flags.stride.max, CFG.flags.stride.per * (att.moved ?? 0));
    if (att.riposte) m *= CFG.flags.riposte;
    if (target.kind !== 'core' && target.kind !== 'building') {
      if (hasFlag(att, 'execution') && target.hp <= target.hpMax * 0.35) m *= CFG.flags.execution;
      if (sk && sk.execute && target.hp <= target.hpMax * 0.4) m *= sk.execute;
    }
    if (target.kind === 'hero') m *= tierValue(b, attSide, 'powerMult') * (hasFlag(att, 'duelist') ? CFG.flags.duelist : 1) * (spec.mult.hero ?? 1);
    if (target.kind === 'building') m *= 1 + compOf(att, 'trap');
    // 式神の奥義：次の自分の攻撃の与ダメ倍率（自分の手番の攻撃だけ。反撃には乗らない）
    if (att.ougiPower && b.current === att.id) m *= att.ougiPower;
    // 賽3つ：この手番の役・賽の条件の与ダメと、術の賽（自分の手番の攻撃だけ。反撃には乗らない）
    if (b.current === att.id) m *= (att.rollAtk ?? 1) * (att.skillDice ?? 1);
    if (inside) m *= footing(b).dealt ?? 1;
    m *= bannerMult(b, att);
    if (hasStatus(att, 'might')) m *= CFG.status.might;
    if (att.spec?.boon?.key === 'atk' && att.boonStacks) m *= 1 + att.spec.boon.value * att.boonStacks;
  }
  if (magic) def = Math.floor(def / 2);
  // 灯籠陣：自分の与ダメが上がる
  if (att.kind === 'hero' && b.zones?.length) m *= lanternDealt(b, att);
  let dmg = Math.max(1, Math.floor((Math.floor(att.atk * m) - def) * after));
  if (target.kind === 'hero') {
    const ts = target.side as Side;
    const boonDef = target.spec?.boon?.key === 'def' && target.boonStacks ? Math.max(0.3, 1 - target.spec.boon.value * target.boonStacks) : 1;
    const taken = (onOwnTile(b, target) ? footing(b).taken ?? 1 : 1) * tierValue(b, ts, 'guard') * (target.spec?.mult.taken ?? 1) * (hasStatus(target, 'ward') ? CFG.status.ward : 1) * (target.rollGuard ?? 1) * boonDef;
    const guard = hasFlag(target, 'fushin_guard') && target.cell != null && buildingsOf(b, ts).some((u) => adjacent(u.cell as number, target.cell as number)) ? 1 - CFG.flags.fushinGuard : 1;
    if (taken * guard !== 1) dmg = Math.max(1, Math.ceil(dmg * taken * guard));
  }
  if (target.kind === 'building' && leadBy(b, target)) dmg = Math.max(1, Math.ceil(dmg * (1 - CFG.flags.fushinLead)));
  // 版3・守りの普請（鬼の棟梁）：自分の本陣は2マス以内の自分の建物1つごとに被ダメが下がる（coreGuardMult）
  if (target.kind === 'core' && rulesOf(b) >= 3 && isUnitSide(target.side)) {
    const m = coreGuardMult(b, target.side as Side);
    if (m < 1) dmg = Math.max(1, Math.ceil(dmg * m));
  }
  if (target.kind !== 'core' && target.defending) dmg = Math.max(1, Math.ceil(dmg / 2));
  // 術：月下の構え（被ダメの倍率。0 なら受けない）・灯籠陣（自分と陣の中の自分の建物）
  if (target.kind === 'hero' && target.stance) dmg = target.stance.taken <= 0 ? 0 : Math.max(1, Math.ceil(dmg * target.stance.taken));
  if (target.kind === 'hero' || target.kind === 'building') {
    const lt = lanternTaken(b, target);
    if (lt < 1 && dmg > 0) dmg = Math.max(1, Math.ceil(dmg * lt));
  }
  // 重装の鎧：物理の攻撃はほとんど通らない（魔法は鎧を無視）
  if (target.kind !== 'core' && !magic && armored(target, att)) dmg = Math.max(1, Math.ceil(dmg * CFG.enemyTypes.heavy.taken));
  return dmg;
}

/** 重装の鎧が att の攻撃を受け止める（鎧が残っていて、味方どうしでない） */
export const armored = (target: Actor, att: Actor) => (target.armor ?? 0) > 0 && att.side !== target.side;

/** 敵の型の初期状態（重装の鎧）。中立・ガーディアン・増援・攻め手を作ったときに呼ぶ */
export function equipEnemyType<T extends Actor>(a: T): T {
  const type = a.enemyId ? ENEMIES[a.enemyId]?.type : undefined;
  if (type === 'heavy') a.armor = a.armorMax = CFG.enemyTypes.heavy.armor[a.tier ?? 'small'] ?? 1;
  return a;
}

/** 範囲の割合（術は特性込み、通常攻撃は spec.splash） */
export const splashRatio = (a: Actor, useSkill: boolean) =>
  !a.spec ? 0 : useSkill ? skillSplash(a) : Math.max(a.spec.splash, hasFlag(a, 'eq_cleave') ? CFG.flags.eqCleave : 0);
/** 範囲・貫通がある攻撃（反撃を受けない） */
export const isAreaAttack = (a: Actor, useSkill: boolean) => splashRatio(a, useSkill) > 0 || (useSkill && !!a.spec?.skill.pierce);

/** 的のほかに当たる相手（貫通：的までの直線の上と的の奥、範囲：的の周り）。dmg は予測と同じ計算 */
export function extraHits(b: Battle, att: Actor, target: Target, useSkill: boolean): { u: Actor; dmg: number; pierce: boolean }[] {
  if (!att.spec || target.kind === 'core' || att.cell == null || target.cell == null) return [];
  const foe = (u: Actor | undefined): u is Actor => !!u && u.alive && u.side !== att.side && u.type !== 'otoshiana' && u.id !== target.id;
  const out: { u: Actor; dmg: number; pierce: boolean }[] = [];
  const pierce = useSkill ? att.spec.skill.pierce + (att.ougiCasting ? (ougiOf(att)?.pierce ?? 0) : 0) : 0;
  if (pierce > 0 && onLine(att.cell, target.cell)) {
    // 自分から的までの直線の上の相手と、的の奥 pierce マスの相手
    const d = dirToward(att.cell, target.cell);
    const reach = dist(att.cell, target.cell) + pierce;
    let c: number | undefined = att.cell;
    for (let k = 0; k < reach; k++) {
      c = step(c, d);
      if (c == null) break;
      const u = unitAt(b, c);
      if (foe(u)) out.push({ u, dmg: damageCalc(b, att, u, true), pierce: true });
    }
  }
  const ratio = splashRatio(att, useSkill);
  if (ratio > 0)
    for (const u of b.actors)
      if (foe(u) && u.cell != null && adjacent(u.cell, target.cell) && !out.some((h) => h.u === u))
        out.push({ u, dmg: damageCalc(b, att, u, false, ratio), pierce: false });
  return out;
}

export interface Forecast {
  dmg: number;
  /** 重装の鎧で減った（物理の攻撃） */
  armor?: boolean;
  hpBefore: number;
  hpAfter: number;
  kill: boolean;
  counter: number; // 相手が今の位置から届く場合の反撃見込み（相手の次の手番）
  /** この攻撃にすぐ返ってくる反撃（反撃の銘・返し・あまのじゃく・月下の構え。倒すときや範囲攻撃では0） */
  riposte: number;
  footing: boolean; // 自陣の中の与ダメ補正が乗る
  guarded: boolean; // 相手が自陣の中で被ダメ軽減
  splash: { id: number; dmg: number }[];
  /** この攻撃でかける状態異常（鳥居のそばの相手にはかからないので空） */
  inflict: { key: StatusKey; turns: number; dmg?: number }[];
}

export function forecastFor(b: Battle, att: Actor, ref: TargetRef, useSkill: boolean, ougi = false): Forecast | null {
  if (ougi) return castingOugi(att, () => forecastFor(b, att, ref, true));
  const target = resolveTarget(b, ref);
  if (!target) return null;
  const dmg = damageCalc(b, att, target, useSkill);
  const hp = target.hp;
  const out: Forecast = {
    dmg,
    hpBefore: hp,
    hpAfter: Math.max(0, hp - dmg),
    kill: dmg >= hp,
    counter: 0,
    riposte: 0,
    footing: onOwnTile(b, att) && (footing(b).dealt ?? 1) > 1,
    guarded: target.kind !== 'core' && onOwnTile(b, target) && (footing(b).taken ?? 1) < 1,
    splash: [],
    inflict: [],
  };
  if (target.kind !== 'core' && armored(target, att)) out.armor = true;
  if (useSkill && hasFlag(att, 'ougi:yuzuha_kekkai') && target.kind !== 'core' && target.kind !== 'building' && !out.kill && !toriiWard(b, target))
    out.inflict.push({ key: 'burn', turns: 1, dmg: Math.max(1, Math.round(Math.min(dmg, hp) * CFG.flags.kekkaiBurn)) });
  const living = target.kind !== 'core' && target.kind !== 'building' && !out.kill && !toriiWard(b, target);
  if (living && useSkill && hasFlag(att, 'eq_ignite')) out.inflict.push({ key: 'burn', turns: 1, dmg: Math.max(1, Math.round(Math.min(dmg, hp) * CFG.flags.eqIgnite)) });
  if (living && !useSkill && hasFlag(att, 'eq_bind')) out.inflict.push({ key: 'bind', turns: CFG.flags.eqBind });
  const cburn = compOf(att, 'burn');
  if (cburn > 0 && target.kind !== 'core' && target.kind !== 'building' && !out.kill && !toriiWard(b, target))
    out.inflict.push({ key: 'burn', turns: CFG.flags.compBurnTurns, dmg: Math.max(1, Math.round(Math.min(dmg, hp) * cburn)) });
  if (target.kind !== 'core' && target.kind !== 'building' && !out.kill && target.cell != null && dist(target.cell, att.cell as number) <= attackRange(target))
    out.counter = damageCalc(b, target, att, false);
  out.splash = extraHits(b, att, target, useSkill).map((h) => ({ id: h.u.id, dmg: h.dmg }));
  if (target.kind === 'hero' && !out.kill && !isAreaAttack(att, useSkill)) out.riposte = riposteDamage(b, target, att);
  return out;
}

export function cmdAttack(b: Battle, ref: TargetRef, useSkill = false, ougi = false): CmdResult {
  if (ougi) {
    const a = heroTurn(b);
    if (!a || !canAct(b) || a.dashing) return fail('phase');
    const why = ougiBlock(b, a);
    if (why) return fail(why);
    const r = castingOugi(a, () => cmdAttack(b, ref, true, false));
    return r;
  }
  const a = heroTurn(b);
  if (!a || !canAct(b) || a.dashing) return fail('phase');
  if (useSkill && !a.ougiCasting && !skillReady(a)) return fail('cooldown');
  const legal = attackTargetsAt(b, a, a.cell as number, useSkill).some((t) =>
    t.kind === 'core' ? ref.kind === 'core' && t.side === ref.side : ref.kind === 'actor' && t.id === ref.id,
  );
  if (!legal) return fail('range');
  const ev: Ev = [];
  // 版3・敵の武将の術は相手の武将には「構え」てからでないと撃てない：構えていなければ通常攻撃になり、手番の終わりに構える（rivalChargeAtEnd）。
  // 構えた相手へ撃つのが「放つ」（strike）
  if (useSkill && !a.ougiCasting && ref.kind === 'actor' && rivalCharges(b, a) && resolveTarget(b, ref)?.kind === 'hero') {
    if (a.telegraph?.wait.some((w) => w.id === ref.id)) {
      const t = resolveTarget(b, ref) as Actor;
      ev.push({ t: 'strike', id: a.id, cells: [t.cell as number] });
      a.telegraph = null;
      if (CFG.v3.rivalCharge.mult !== 1) a.ougiPower = (a.ougiPower ?? 1) * CFG.v3.rivalCharge.mult;
    } else {
      if (!attackTargetsAt(b, a, a.cell as number, false).some((t) => t.kind === 'actor' && t.id === ref.id)) return fail('range');
      useSkill = false;
    }
  }
  // 試作：溜めの要る術。溜めていなければ、この手番は溜めて終わる
  if (useSkill && !a.ougiCasting && CFG.chargeSkill.includes(a.spec!.key) && !a.charged && (!CFG.chargeCoreOnly || ref.kind === 'core')) {
    a.charged = true;
    ev.push({ t: 'charge', id: a.id });
    endTurn(b, a, ev);
    return ok(ev);
  }
  if (useSkill && a.charged && (!CFG.chargeCoreOnly || ref.kind === 'core')) a.charged = false;
  const target = resolveTarget(b, ref)!;
  if (useSkill) skillDice(b, a, ev);
  try {
    return attackWith(b, a, ref, target, useSkill, ev);
  } finally {
    a.skillDice = undefined;
  }
}

function attackWith(b: Battle, a: Actor, ref: TargetRef, target: Target, useSkill: boolean, ev: Ev): CmdResult {
  const sk = a.spec!.skill;
  // 着弾点（倒すと cell が消えるので、当てる前に覚えておく）
  const hitCell = target.kind !== 'core' && typeof target.cell === 'number' ? target.cell : null;
  const dmg = damageCalc(b, a, target, useSkill);
  const splash = extraHits(b, a, target, useSkill);
  // 式神の奥義の「次の攻撃」はこの攻撃で使い切る（この攻撃の途中で奥義が出たときは次に残す）
  const powerUsed = a.ougiPower;
  let cdCut = 0;
  if (useSkill) {
    // お供・術の待ち-1（comp:cd）：回数まで、術の後の待ちが1手番短い（最低1）
    const cool = skillCooldown(b, a);
    if (cool > 1 && compOf(a, 'cd') > 0 && useComp(a, 'cd')) cdCut = 1;
    a.skillReady = (a.turns ?? 0) + cool - cdCut;
  }
  const og = a.ougiCasting ? ougiOf(a) : undefined;
  ev.push({ t: 'attack', id: a.id, target: ref, skill: useSkill ? (og ? (a.spec!.ougiName ?? og.name) : sk.name) : null, dmg, ...(og ? { ougi: true } : {}) });
  if (cdCut) ev.push({ t: 'companion', id: a.id, ability: 'cd', amount: cdCut });
  let dealt = 0;
  let mainDealt = 0;
  if (target.kind === 'core') {
    if (b.siege[a.side as Side] > 0) b.siege[a.side as Side]--;
    hitCore(b, target.side, dmg, a, ev);
  } else {
    mainDealt = dealt = hitActor(b, target, dmg, a, ev);
    if (useSkill && sk.delay) delayActor(b, target, skillDelay(a), ev);
    if (useSkill && hasFlag(a, 'ougi:yuzuha_kekkai') && target.alive && mainDealt > 0)
      afflict(b, target, 'burn', 1, ev, { dmg: Math.max(1, Math.round(mainDealt * CFG.flags.kekkaiBurn)), from: a.id });
    // お供・火傷を付ける（comp:burn）：当てた相手（ヒーロー・妖）に与ダメの割合の火傷
    const cburn = compOf(a, 'burn');
    if (cburn > 0 && target.alive && mainDealt > 0 && target.kind !== 'building' && afflict(b, target, 'burn', CFG.flags.compBurnTurns, ev, { dmg: Math.max(1, Math.round(mainDealt * cburn)), from: a.id }))
      ev.push({ t: 'companion', id: a.id, ability: 'burn', amount: Math.max(1, Math.round(mainDealt * cburn)), bid: target.id });
    // 装備の系統の固有：提灯杖（術で火傷）・鎖鎌（足止め）・金棒（遅らせる）・小太刀（追撃）
    if (mainDealt > 0 && target.alive && target.kind !== 'building') {
      if (useSkill && hasFlag(a, 'eq_ignite')) afflict(b, target, 'burn', 1, ev, { dmg: Math.max(1, Math.round(mainDealt * CFG.flags.eqIgnite)), from: a.id });
      if (!useSkill && hasFlag(a, 'eq_bind')) afflict(b, target, 'bind', CFG.flags.eqBind, ev, { from: a.id });
      if (!useSkill && hasFlag(a, 'eq_stagger')) delayActor(b, target, CFG.flags.eqStagger, ev);
    }
    // 羽団扇（eq_knock）：通常攻撃で1マス押し出す（番人・建物は押せない。押せないときは追加のダメージ）
    if (!useSkill && hasFlag(a, 'eq_knock') && target.alive && target.kind !== 'boss' && target.kind !== 'building' && target.cell != null && a.cell != null && !over(b)) {
      const from = target.cell;
      const nx = step(from, dirToward(a.cell, from));
      if (nx != null && openCell(b, nx)) {
        target.cell = nx;
        ev.push({ t: 'push', id: target.id, from, to: nx });
        delayActor(b, target, CFG.flags.eqKnockDelay, ev);
      } else dealt += hitActor(b, target, Math.max(1, Math.round(damageCalc(b, a, target, false, CFG.flags.eqKnockWall))), a, ev);
    }
    if (!useSkill && hasFlag(a, 'eq_double') && target.alive && a.alive && !over(b)) {
      const d2 = damageCalc(b, a, target, false, CFG.flags.eqDouble);
      ev.push({ t: 'splash', id: a.id, cell: target.cell as number });
      dealt += hitActor(b, target, d2, a, ev);
    }
    if (!isAreaAttack(a, useSkill)) counterAttack(b, target, a, ev);
    if (target.alive && !over(b)) towerVolley(b, a, target, ev);
  }
  if (((useSkill && hasFlag(a, 'ougi:akari_lantern')) || (!useSkill && hasFlag(a, 'eq_paint'))) && hitCell != null && !over(b)) {
    // 灯し火（術）・御幣（eq_paint、通常攻撃）：着弾点と周りを塗る（本陣が的のときは塗らない。倒した相手のマスも塗る）
    const at = hitCell;
    const area = useSkill && hasFlag(a, 'akari_lantern2') ? b.tiles.map((_, i) => i).filter((i) => dist(i, at) <= 2) : [at, ...neighbors(at)];
    const cells = area.filter((c) => paintable(b, c) && b.tiles[c].owner !== a.side);
    for (const c of cells) b.tiles[c].owner = a.side as Side;
    if (cells.length) ev.push({ t: 'paint', id: a.id, side: a.side as Side, cells });
  }
  if (!over(b))
    for (const hit of splash)
      if (hit.u.alive && !over(b)) {
        ev.push({ t: 'splash', id: a.id, cell: hit.u.cell as number });
        dealt += hitActor(b, hit.u, hit.dmg, a, ev);
        if (useSkill && sk.delay && (hit.pierce || hasFlag(a, 'ougi:kazamaru_senpu'))) delayActor(b, hit.u, skillDelay(a), ev);
      }
  if (a.alive) {
    const tm = traitMods(a);
    const skLeech = sk.leech + (sk.leech ? (tm.leech ?? 0) : 0) + (og?.leech ?? 0);
    const leech = Math.round(dealt * (a.spec!.lifesteal + (tm.lifesteal ?? 0))) + (useSkill && skLeech ? Math.max(1, Math.round(mainDealt * skLeech)) : 0);
    if (leech > 0 && dealt > 0) heal(a, leech, ev, 'leech');
  }
  if (powerUsed && a.ougiPower === powerUsed) a.ougiPower = undefined;
  // 式神の奥義：ボスに初めて攻撃した・敵本陣に初めて攻撃した
  if (!over(b) && a.alive) {
    if (target.kind === 'core') tryOugi(b, a, 'core', ev);
    else if (target.kind === 'boss') tryOugi(b, a, 'boss', ev);
    if (useSkill && !over(b)) tryOugi(b, a, 'skill', ev);
  }
  a.riposte = false;
  if (useSkill && sk.guard) {
    a.defending = true;
    if (hasFlag(a, 'riposte')) a.riposte = true;
    ev.push({ t: 'defend', id: a.id });
  }
  if (useSkill && sk.heal && a.alive) heal(a, Math.round(a.hpMax * sk.heal), ev, 'skill');
  // 奥義を放ったら妖力は段階1に戻る（特性も消えるので、一撃離脱もしない）
  if (og && !over(b)) dropSurge(b, a.side as Side, ev);
  const dash = Math.max(traitMods(a).dash ?? 0, hasFlag(a, 'hitrun') ? CFG.flags.hitrun : 0);
  if (dash > 0 && a.alive && !over(b) && b.current === a.id) {
    // 一撃離脱：攻撃の後に dash マスまで動ける（move か wait で手番が終わる）
    a.dashing = true;
    b.roll = null;
    b.bonus = 0;
    b.budget = dash;
    const reach = computeReach(b, a, dash);
    b.reach = [...reach].map(([cell, v]) => [cell, v.d, v.prev ?? -1, v.stop ? 1 : 0]);
    b.phase = 'rolled';
    ev.push({ t: 'dash', id: a.id, budget: dash });
    return ok(ev);
  }
  endTurn(b, a, ev);
  return ok(ev);
}

function delayActor(b: Battle, t: Actor, delay: number, ev: Ev) {
  if (!t.alive || t.kind === 'building') return;
  const amount = Math.round((interval(b, t) * delay) / 1000);
  t.nextAt += amount;
  ev.push({ t: 'delay', id: t.id, amount });
}

/** 反撃（bu_counter）：隣から攻撃されて生き残ったヒーローが、通常攻撃の一部で殴り返す */
/** 攻撃された target がすぐ返す反撃のダメージ（返さなければ 0）。counterAttack と同じ条件 */
export function riposteDamage(b: Battle, target: Actor, att: Actor): number {
  if (target.kind !== 'hero' || !target.alive || !att.alive || att.kind === 'building') return 0;
  const flag = Math.max(hasFlag(target, 'bu_counter') || hasFlag(target, 'counter') ? CFG.flags.counter : 0, hasFlag(target, 'kaeshi') ? CFG.flags.kaeshi : 0);
  const mult = Math.max(flag, compOf(target, 'mirror'), target.stance?.counter ?? 0);
  if (mult <= 0 || target.cell == null || att.cell == null || !adjacent(target.cell, att.cell)) return 0;
  return damageCalc(b, target, att, false, mult);
}

export function counterAttack(b: Battle, target: Actor, att: Actor, ev: Ev) {
  if (over(b) || target.kind !== 'hero' || !target.alive || !att.alive || att.kind === 'building') return;
  const flag = Math.max(hasFlag(target, 'bu_counter') || hasFlag(target, 'counter') ? CFG.flags.counter : 0, hasFlag(target, 'kaeshi') ? CFG.flags.kaeshi : 0);
  // お供・あまのじゃく返し（comp:mirror）：隣からの攻撃に、この割合の力で反撃（技の反撃と重ならず、大きい方）
  const mirror = compOf(target, 'mirror');
  // 月下の構え：隣からの攻撃に、構えの倍率で斬り返す
  const mult = Math.max(flag, mirror, target.stance?.counter ?? 0);
  if (mult <= 0) return;
  if (target.cell == null || att.cell == null || !adjacent(target.cell, att.cell)) return;
  const dmg = damageCalc(b, target, att, false, mult);
  if (mirror > flag) ev.push({ t: 'companion', id: target.id, ability: 'mirror', amount: dmg });
  ev.push({ t: 'counter', id: target.id, target: att.id, dmg });
  hitActor(b, att, dmg, target, ev);
}

/** 待機＝防御（次の自分の手番まで被ダメ半分） */
export function cmdWait(b: Battle): CmdResult {
  const a = heroTurn(b);
  if (!a || !canAct(b)) return fail('phase');
  if (a.dashing) {
    // 一撃離脱の移動をやめる（防御にはならない）
    const ev: Ev = [];
    endTurn(b, a, ev);
    return ok(ev);
  }
  a.defending = true;
  if (hasFlag(a, 'riposte')) a.riposte = true;
  const ev: Ev = [{ t: 'defend', id: a.id }];
  endTurn(b, a, ev);
  return ok(ev);
}

/** 古い操作（薬を飲んで手番終了）。今の画面・AIは item を使う。保存済みの戦闘の再生のために残す */
export function cmdPotion(b: Battle): CmdResult {
  const a = heroTurn(b);
  if (!a || !canAct(b) || a.dashing) return fail('phase');
  if (itemCount(a, 'heal') <= 0) return fail('empty');
  if (a.hp >= a.hpMax) return fail('full');
  setItemCount(a, 'heal', itemCount(a, 'heal') - 1);
  const ev: Ev = [{ t: 'potion', id: a.id }];
  heal(a, a.spec!.potionHeal, ev, 'potion');
  endTurn(b, a, ev);
  return ok(ev);
}

/** アイテムの残り（古い保存データは potions が回復薬の数） */
export const itemCount = (a: Actor, id: ItemId) => (a.items ? (a.items[id] ?? 0) : id === 'heal' ? (a.potions ?? 0) : 0);
function setItemCount(a: Actor, id: ItemId, n: number) {
  (a.items ??= a.potions ? { heal: a.potions } : {})[id] = n;
  if (id === 'heal') a.potions = n;
}

/** そのアイテムを今使えないわけ（使えるなら null） */
export function itemBlock(b: Battle, a: Actor | undefined, id: ItemId): string | null {
  if (!a || a.kind !== 'hero' || b.current !== a.id || !(b.phase === 'start' || b.phase === 'rolled' || b.phase === 'moved') || a.dashing) return 'phase';
  if (a.itemUsed) return 'used';
  if (itemCount(a, id) <= 0) return 'empty';
  if (id === 'heal' && a.hp >= a.hpMax) return 'full';
  if (id === 'swift' && b.phase === 'moved') return 'moved';
  if (id === 'purify' && !BAD_STATUS_KEYS.some((k) => hasStatus(a, k)) && hasStatus(a, 'blessed')) return 'nothing';
  return null;
}

/** アイテムを使う（手番は終わらない・1手番に1回） */
export function cmdItem(b: Battle, id: ItemId): CmdResult {
  const a = heroTurn(b);
  const why = itemBlock(b, a, id);
  if (why) return fail(why);
  const side = a!.side as Side;
  const D = CFG.items[id];
  setItemCount(a!, id, itemCount(a!, id) - 1);
  a!.itemUsed = true;
  const ev: Ev = [{ t: 'item', id: a!.id, item: id }];
  if (id === 'heal') heal(a!, a!.spec!.potionHeal, ev, 'potion');
  else if (id === 'power') buff(a!, 'might', D.turns ?? 2, ev);
  else if (id === 'guard') buff(a!, 'ward', D.turns ?? 2, ev);
  else if (id === 'purify') {
    cleanse(a!, ev, 'item');
    buff(a!, 'blessed', D.turns ?? 2, ev);
  } else if (id === 'spirit') gainSurge(b, side, D.points ?? 2, ev);
  else if (id === 'swift') {
    const m = D.move ?? 2;
    if (b.phase === 'start') b.bonus += m;
    else {
      // 賽を振った後：歩数を足して届くマスを出し直す
      b.budget += m;
      const reach = computeReach(b, a!, b.budget);
      b.reach = [...reach].map(([cell, v]) => [cell, v.d, v.prev ?? -1, v.stop ? 1 : 0]);
    }
  }
  return ok(ev);
}

// ---------- 建築 ----------

export const buildCost = (a: Actor, type: BuildingType) => (a.freeBuild ? 0 : Math.max(1, Math.round(CFG.buildings[type].cost * (a.spec?.build.costMult ?? 1)) - (a.spec?.build.costFlat ?? 0)));
export const buildLevel = (a: Actor, type: BuildingType) => Math.max(0, a.spec?.build.levels[type] ?? 0);

/** 建てられるマス：自分の陣地の空いた草地、建築範囲内、本陣の隣は不可、種類ごとの条件 */
export function buildCellsFor(b: Battle, a: Actor, type: BuildingType): number[] {
  const def = CFG.buildings[type];
  if (!def || !a.spec || a.cell == null) return [];
  const side = a.side as Side;
  const reach = a.spec.build.range;
  const out: number[] = [];
  for (let i = 0; i < b.tiles.length; i++) {
    const t = b.tiles[i];
    if (t.owner !== side || t.t !== 'grass' || t.relay || t.pickup || i === a.cell) continue;
    if (dist(i, a.cell) > reach || dist(i, b.cores.blue.cell) <= 1 || (!b.defense && dist(i, b.cores.red.cell) <= 1)) continue;
    if (def.near != null && dist(i, b.cores[side].cell) > def.near) continue;
    if (def.inside && !insideTerritory(b, i, side)) continue;
    if (b.actors.some((u) => u.alive && u.cell === i)) continue;
    out.push(i);
  }
  return out;
}

/** 建てられない理由（建てられるなら null） */
export function canBuildFor(b: Battle, a: Actor | undefined, type: BuildingType): string | null {
  if (!a || a.kind !== 'hero' || !canAct(b) || a.dashing || !BUILDING_TYPES.includes(type)) return 'phase';
  if (!a.spec!.build.unlocked.includes(type)) return 'locked';
  if ((a.builtThisTurn ?? 0) >= buildPerTurn(a)) return 'done';
  if (buildingsOf(b, a.side as Side).length >= buildMaxCount(a)) return 'limit';
  if (b.mat[a.side as Side] < buildCost(a, type)) return 'mat';
  if (!buildCellsFor(b, a, type).length) return noCellReason(b, a, type);
  return null;
}

/** 建てられるマスが無いとき、どの条件で外れたか（近くのマスを条件の順に絞り、最初に0になった条件） */
function noCellReason(b: Battle, a: Actor, type: BuildingType): string {
  const def = CFG.buildings[type];
  const side = a.side as Side;
  const at = a.cell as number;
  let cells = b.tiles.map((_, i) => i).filter((i) => i !== at && dist(i, at) <= a.spec!.build.range);
  const steps: [string, (i: number) => boolean][] = [
    ['cells_own', (i) => b.tiles[i].owner === side],
    ['cells_ground', (i) => b.tiles[i].t === 'grass' && !b.tiles[i].relay && !b.tiles[i].pickup],
    ['cells_core', (i) => dist(i, b.cores.blue.cell) > 1 && (!!b.defense || dist(i, b.cores.red.cell) > 1)],
    ['cells_near', (i) => def.near == null || dist(i, b.cores[side].cell) <= def.near],
    ['cells_inside', (i) => !def.inside || insideTerritory(b, i, side)],
    ['cells_unit', (i) => !b.actors.some((u) => u.alive && u.cell === i)],
  ];
  for (const [why, ok] of steps) {
    cells = cells.filter(ok);
    if (!cells.length) return why;
  }
  return 'cells';
}

/** 建物の性能（建てた時点で決まる） */
export function buildingStats(a: Actor, type: BuildingType, coreMax: number) {
  const def = CFG.buildings[type];
  const s = a.spec!;
  const lv = buildLevel(a, type);
  const lvMult = 1 + CFG.buildLvStep * lv;
  const tm = traitMods(a);
  const hp = def.hp <= 1 ? 1 : Math.max(1, Math.round(def.hp * s.build.hpMult * lvMult * (a.hpMax / CFG.buildHpBase) * (1 + (tm.buildHp ?? 0))));
  const pm = s.build.powerMult * lvMult;
  let power = 0;
  if (def.dmg) power = Math.max(1, Math.round(def.dmg * a.atk * pm * (isShooter(type) ? 1 + (tm.yagura ?? 0) : 1)));
  else if (def.repair) power = Math.max(1, Math.round(def.repair * coreMax * pm));
  else if (def.aura) power = def.aura * pm;
  else if (def.heal) power = def.heal * pm;
  const range = def.range ? def.range + (lv >= 5 ? 1 : 0) : 0;
  return { hp, power, range, level: lv, def: def.def };
}

/** 自分の建物を壊す（離れていても。手番は使わない。陣材は戻らない） */
export function cmdDemolish(b: Battle, cell: number): CmdResult {
  const a = heroTurn(b);
  if (!a || !canAct(b) || a.dashing) return fail('phase');
  const u = b.actors.find((x) => x.alive && x.kind === 'building' && x.side === a.side && x.cell === cell);
  if (!u) return fail('target');
  u.alive = false;
  u.hp = 0;
  u.cell = null;
  return ok([{ t: 'death', id: u.id, cell }]);
}

export function cmdBuild(b: Battle, type: BuildingType, cell: number): CmdResult {
  const a = heroTurn(b);
  const why = canBuildFor(b, a, type);
  if (why) return fail(why);
  if (!buildCellsFor(b, a!, type).includes(cell)) return fail('cell');
  const side = a!.side as Side;
  b.mat[side] -= buildCost(a!, type);
  const st = buildingStats(a!, type, b.cores[side].max);
  const bld: Actor = {
    id: b.nextId++,
    kind: 'building',
    side,
    enemyId: null,
    cell,
    hp: st.hp,
    hpMax: st.hp,
    atk: 0,
    def: st.def,
    agi: 1,
    alive: true,
    nextAt: 0,
    defending: false,
    type,
    power: st.power,
    range: st.range,
    level: st.level,
  };
  b.actors.push(bld);
  b.prio[bld.id] = Object.keys(b.prio).length;
  if (side === 'blue') b.stats.buildingsBuilt++;
  else b.stats.rivalBuildingsBuilt++;
  a!.builtThisTurn = (a!.builtThisTurn ?? 0) + 1;
  const ev: Ev = [{ t: 'build', id: a!.id, bid: bld.id, type, cell, side }];
  // 式神の加勢（建てたとき）
  tryOugi(b, a!, 'build', ev);
  return ok(ev);
}

/** 攻撃の建物（櫓・藁人形・焙烙台・大筒） */
export const isShooter = (t: BuildingType | undefined) => !!t && !!CFG.buildings[t]?.dmg && !!CFG.buildings[t]?.range;
/** 攻撃の建物の的の候補（射程の中・敵ヒーロー優先・近い順） */
function towerTargets(b: Battle, u: Actor, side: Side): Actor[] {
  const foes = b.actors.filter((x) => x.alive && x.cell != null && x.kind !== 'building' && x.side !== side && dist(x.cell, u.cell as number) <= (u.range ?? 2));
  return foes.sort((x, y) => Number(y.kind === 'hero') - Number(x.kind === 'hero') || dist(x.cell!, u.cell!) - dist(y.cell!, u.cell!) || x.id - y.id);
}
/** 攻撃の建物が t を撃つ（mult は威力の倍率。焙烙台は周りにも・燃やす、藁人形は呪い・足止め） */
function towerFire(b: Battle, u: Actor, t: Actor, mult: number, ev: Ev) {
  const def = CFG.buildings[u.type!];
  // 灯籠陣の中の自分の建物は威力が上がる
  const power = Math.max(1, Math.round((u.power ?? 1) * mult * (leadBy(b, u) ? 1 + CFG.flags.fushinLead : 1) * lanternDealt(b, u)));
  const at = t.cell as number;
  ev.push({ t: 'towerShot', id: u.id, target: t.id, cell: at });
  const got = hitActor(b, t, power, u, ev);
  // 雷太鼓：近くの敵へ跳ねる
  if (def.chain && !over(b)) {
    const near = b.actors
      .filter((x) => x.alive && x.cell != null && x !== t && x.kind !== 'building' && x.side !== u.side && x.type !== 'otoshiana' && dist(x.cell, at) <= 2)
      .sort((x, y) => dist(x.cell!, at) - dist(y.cell!, at) || x.id - y.id)
      .slice(0, def.chain);
    for (const x of near) {
      if (over(b)) break;
      ev.push({ t: 'splash', id: u.id, cell: x.cell as number });
      hitActor(b, x, Math.max(1, Math.round(power * (def.chainMult ?? 0.7))), u, ev);
    }
  }
  if (def.burn && t.alive && got > 0) afflict(b, t, 'burn', 1, ev, { dmg: Math.max(1, Math.round(got * def.burn)), from: u.id });
  if (def.curse && t.alive) {
    afflict(b, t, 'curse', def.curse, ev, { from: u.id });
    afflict(b, t, 'bind', 1, ev, { from: u.id });
  }
  if (def.splash && !over(b))
    for (const x of b.actors.filter((x) => x.alive && x.cell != null && x !== t && x.kind !== 'building' && x.side !== u.side && x.type !== 'otoshiana' && adjacent(x.cell, at))) {
      if (over(b)) break;
      ev.push({ t: 'splash', id: u.id, cell: x.cell as number });
      const g = hitActor(b, x, Math.max(1, Math.round(power * def.splash)), u, ev);
      if (def.burn && x.alive && g > 0) afflict(b, x, 'burn', 1, ev, { dmg: Math.max(1, Math.round(g * def.burn)), from: u.id });
    }
}
/** 鳶口（eq_volley）：攻撃した的を射程に入れている自分の攻撃の建物が、eqVolley の威力で撃つ（大筒の弾込めは使わない） */
function towerVolley(b: Battle, a: Actor, t: Actor, ev: Ev) {
  if (!hasFlag(a, 'eq_volley') || t.cell == null) return;
  for (const u of buildingsOf(b, a.side as Side)) {
    if (over(b) || !t.alive || t.cell == null) return;
    if (!isShooter(u.type) || dist(u.cell as number, t.cell) > (u.range ?? 2)) continue;
    towerFire(b, u, t, CFG.flags.eqVolley, ev);
  }
}

/** 持ち主の手番の最初：攻撃の建物が撃つ（敵ヒーロー優先）、修繕小屋が本陣を直す */
/** 守りの普請（fushin_lead）：持ち主のヒーローの隣にある建物 */
function leadBy(b: Battle, u: Actor): boolean {
  if (u.kind !== 'building' || u.cell == null || !isUnitSide(u.side)) return false;
  const h = heroOf(b, u.side as Side);
  return !!h && h.alive && h.cell != null && hasFlag(h, 'fushin_lead') && adjacent(h.cell, u.cell);
}

function buildingPhase(b: Battle, a: Actor, ev: Ev) {
  const side = a.side as Side;
  // 守りの普請（fushin_repair）：周りの自分の建物を直す
  if (hasFlag(a, 'fushin_repair') && a.cell != null)
    for (const u of buildingsOf(b, side)) {
      if (dist(u.cell as number, a.cell) > 2 || u.hp >= u.hpMax) continue;
      const got = Math.min(u.hpMax - u.hp, Math.max(1, Math.round(u.hpMax * CFG.flags.fushinRepair)));
      u.hp += got;
      ev.push({ t: 'heal', id: u.id, amount: got, why: 'repair' });
    }
  for (const u of buildingsOf(b, side)) {
    if (over(b)) return;
    const pulse = u.type ? CFG.buildings[u.type]?.pulse : undefined;
    if (pulse) {
      // 毒霧の壺：まわりの敵すべてに毒
      for (const x of b.actors.filter((x) => x.alive && x.cell != null && x.kind !== 'building' && x.side !== side && x.type !== 'otoshiana' && dist(x.cell, u.cell as number) <= pulse)) {
        if (over(b)) return;
        ev.push({ t: 'splash', id: u.id, cell: x.cell as number });
        hitActor(b, x, Math.max(1, u.power ?? 1), u, ev);
      }
      continue;
    }
    if (isShooter(u.type)) {
      const def = CFG.buildings[u.type!];
      // 大筒：every 手番に1回（撃った次の手番は弾込め）
      if (def.every && def.every > 1) {
        if ((u.reload ?? 0) > 0) {
          u.reload!--;
          continue;
        }
      }
      const t = towerTargets(b, u, side)[0];
      if (!t) continue;
      if (def.every && def.every > 1) u.reload = def.every - 1;
      towerFire(b, u, t, 1, ev);
    } else if (u.type === 'shuzen') {
      const core = b.cores[side];
      const got = Math.min(u.power ?? 0, core.max - core.hp);
      if (got > 0) {
        core.hp += got;
        ev.push({ t: 'coreRepair', side, amount: got, hp: core.hp });
      }
    }
  }
}

// ---------- ダメージ・撃破 ----------

const sideOfAttacker = (att: Actor): Side | null => (isUnitSide(att.side) ? att.side : null);

function hitCore(b: Battle, side: Side, dmg: number, att: Actor, ev: Ev) {
  // 防衛戦の敵本陣は攻め手の出どころの目印（位置だけ）。範囲の術などが届いても削れない
  if (b.defense && side === 'red') return;
  const core = b.cores[side];
  // 試作：結界のある間は本陣を割り切れない（wardFloor）
  const floor = CFG.wardFloor > 0 && wardOn(b, side) ? Math.ceil(core.max * CFG.wardFloor) : 0;
  const cap = CFG.coreHitCap > 0 && att.kind === 'hero' ? Math.ceil(core.max * CFG.coreHitCap) : Infinity;
  const got = Math.max(0, Math.min(dmg, cap, core.hp - floor));
  // 試作：番人が目を覚まし、本陣を殴った相手を追う
  if (CFG.guardianRouse > 0 && att.kind === 'hero')
    for (const g of b.actors) if (g.kind === 'boss' && g.alive && g.side === side && g.roused == null) (g.roused = att.id), (g.engaged = true);
  core.hp -= got;
  if (att.side === 'blue') b.stats.coreDamage += got;
  ev.push({ t: 'coreHit', side, dmg, hp: core.hp });
  if (core.hp > 0) spawnCoreGuards(b, side, ev);
  if (core.hp <= 0) {
    ev.push({ t: 'coreDown', side });
    finish(b, enemyOf(side), side === 'red' ? 'core' : 'lost', ev);
  }
}

/** お供・龍脈の回復（comp:relay） */
function relayHeal(a: Actor, ev: Ev) {
  const v = compOf(a, 'relay');
  if (v <= 0 || !a.alive || a.hp >= a.hpMax) return;
  const amt = Math.round(a.hpMax * v);
  ev.push({ t: 'companion', id: a.id, ability: 'relay', amount: Math.min(amt, a.hpMax - a.hp) });
  heal(a, amt, ev, 'companion');
}

export function hitActor(b: Battle, target: Actor, dmg: number, att: Actor, ev: Ev): number {
  // お供・傘の守り（comp:kasa）：相手からの大きな攻撃（最大HPの kasaMin 以上）を回数だけ防ぐ
  if (target.kind === 'hero' && target.alive && att !== target && att.side !== target.side && dmg >= target.hpMax * CFG.flags.kasaMin) {
    if (compOf(target, 'kasa') > 0 && useComp(target, 'kasa')) {
      ev.push({ t: 'companion', id: target.id, ability: 'kasa', amount: dmg });
      return 0;
    }
    // 式神の奥義で得た守り（傘の守りと同じ判定）
    if ((target.ougiGuard ?? 0) > 0) {
      target.ougiGuard = (target.ougiGuard ?? 0) - 1;
      ev.push({ t: 'companion', id: target.id, ability: 'kasa', amount: dmg });
      return 0;
    }
  }
  // お供・鬼女の執念（comp:rise）：1戦に一度、倒れる攻撃を受けても最大HPの割合で踏みとどまる
  if (target.kind === 'hero' && target.alive && dmg >= target.hp && compOf(target, 'rise') > 0 && useComp(target, 'rise')) {
    const keep = Math.min(target.hpMax, Math.max(1, Math.round(target.hpMax * compOf(target, 'rise'))));
    const dealt = Math.max(0, target.hp - keep);
    target.hp = keep;
    ev.push({ t: 'hit', id: target.id, dmg, hp: target.hp, from: att.id });
    ev.push({ t: 'companion', id: target.id, ability: 'rise', amount: target.hp });
    if (target.hp < target.hpMax * CFG.flags.ougiPinch) tryOugi(b, target, 'pinch', ev);
    return dealt;
  }
  // 守りの普請（fushin_endure）：自分の建物は一度だけ、壊れる攻撃を HP1 で耐える
  if (target.kind === 'building' && !target.endured && dmg >= target.hp && target.hp > 1 && isUnitSide(target.side) && hasFlag(heroOf(b, target.side as Side) ?? target, 'fushin_endure')) {
    target.endured = true;
    const dealt = target.hp - 1;
    target.hp = 1;
    ev.push({ t: 'hit', id: target.id, dmg, hp: target.hp, from: att.id });
    return dealt;
  }
  const dealt = Math.min(dmg, target.hp);
  target.hp -= dealt;
  ev.push({ t: 'hit', id: target.id, dmg, hp: target.hp, from: att.id });
  // 重装の鎧：当たるたびに1枚剥がれる
  if (armored(target, att) && target.hp > 0) {
    target.armor = (target.armor ?? 1) - 1;
    ev.push({ t: 'armor', id: target.id, left: target.armor });
  }
  // 式神の奥義：HPが半分を下回った（倒れていなければ）
  if (target.kind === 'hero' && target.hp > 0 && target.hp < target.hpMax * CFG.flags.ougiPinch && target.hp + dealt >= target.hpMax * CFG.flags.ougiPinch) tryOugi(b, target, 'pinch', ev);
  if (target.kind === 'neutral' || target.kind === 'boss') {
    target.engaged = true;
    target.absence = 0;
    const s = sideOfAttacker(att);
    if (s && target.dmg) {
      target.dmg[s] += dealt;
      target.lastHit = s;
    }
  }
  if (target.hp <= 0) kill(b, target, att, ev);
  return dealt;
}

function countKill(b: Battle, id: string | null) {
  if (!id) return;
  b.stats.kills[id] = (b.stats.kills[id] ?? 0) + 1;
}

function kill(b: Battle, target: Actor, att: Actor, ev: Ev) {
  target.alive = false;
  target.telegraph = null;
  target.status = undefined;
  target.hp = 0;
  ev.push({ t: 'death', id: target.id, cell: target.cell });
  const killer = sideOfAttacker(att);
  if (target.kind === 'building') {
    target.cell = null;
    if (killer === 'blue' && target.side === 'red') b.stats.enemyBuildingsDestroyed++;
    if (killer && killer !== target.side) gainSurge(b, killer, CFG.surge.points.building, ev);
    return;
  }
  if (target.kind === 'hero') {
    const iv = interval(b, target);
    // お供・反魂（comp:hangon）：倒れたとき（回数まで）1手番早く復活
    const early = compOf(target, 'hangon') > 0 && useComp(target, 'hangon') ? 1 : 0;
    if (early) ev.push({ t: 'companion', id: target.id, ability: 'hangon', amount: 1 });
    target.charged = false;
    target.telegraph = null;
    // 倒されると妖力が下がる（CFG.surge.koReset）
    if (CFG.surge.enabled && CFG.surge.koReset !== 'none' && isUnitSide(target.side)) dropSurge(b, target.side, ev, CFG.surge.koReset === 'all' ? 1 : surgeOf(b, target.side).stage - 1);
    target.respawnAt = b.time + Math.max(0, CFG.respawnTime + ((target.spec?.respawnDelta ?? 0) - early) * iv);
    target.defending = false;
    target.riposte = false;
    b.siege[target.side as Side] = 0;
    if (target.side === 'blue') b.stats.heroKOs++;
    else if (killer === 'blue') {
      b.stats.rivalKOs++;
      countKill(b, target.enemyId);
    }
    if (killer && killer !== target.side) {
      gainMat(b, killer, CFG.killMat.hero ?? 0, ev, 'kill');
      gainSurge(b, killer, CFG.surge.points.hero, ev);
      feast(att, ev);
      tryOugi(b, heroOf(b, killer), 'kill', ev);
    }
    target.cell = null;
    if (b.current === target.id && !over(b)) {
      // 自分の手番中に倒れた（落とし穴・狐火）。手番は呼び出し側で終わらせる
    }
    return;
  }
  const cell = target.cell as number;
  // 番人が倒れたら持ち場は空き地になり、倒した側の陣地になる
  if (target.kind === 'boss') {
    b.tiles[cell].t = 'grass';
    b.tiles[cell].owner = killer ?? null;
  }
  // 中央キャンプの番人を倒した側が龍脈を取る
  const guarded = b.tiles[cell];
  if (guarded.relay && killer && guarded.owner !== killer) {
    guarded.owner = killer;
    const h = att.kind === 'hero' ? att : heroOf(b, killer);
    ev.push({ t: 'capture', id: h?.id ?? att.id, cell, side: killer });
    if (h) relayHeal(h, ev);
  }
  target.cell = null;
  target.respawnAt = null;
  if (target.attacker) target.attacker = false;
  if (killer === 'blue') countKill(b, target.enemyId);
  if (killer) gainMat(b, killer, CFG.killMat[target.tier ?? 'small'] ?? 0, ev, 'kill');
  if (killer) feast(att, ev);
  if (killer) devour(att, ev);
  if (killer) {
    if (target.kind === 'boss') gainSurge(b, killer, 0, ev, CFG.surge.bossStages);
    else {
      gainSurge(b, killer, CFG.surge.points.neutral, ev);
      // お供・管狐の囁き（comp:kuda）：中立を倒すと撃破ポイント+1（回数まで）
      const h = heroOf(b, killer);
      if (h && CFG.surge.enabled && compOf(h, 'kuda') > 0 && useComp(h, 'kuda')) {
        ev.push({ t: 'companion', id: h.id, ability: 'kuda', amount: 1 });
        gainSurge(b, killer, 1, ev);
      }
    }
  }
  if (target.kind === 'boss' && killer) {
    if (att.kind === 'hero') {
      b.siege[killer] = CFG.siege.hits;
      ev.push({ t: 'siege', side: killer });
    }
    if (killer === 'blue') b.stats.bossKilled = true;
  }
}

/** 喰らう（敵の武将の傾向「妖狩り」の性質 spec.devour。版3）：倒した妖の数ぶん攻撃・最大HPに掛かる倍率 */
export const devourMult = (a: Actor) => 1 + (a.spec?.devour ?? 0) * (a.devoured ?? 0);
/** 妖を倒した武将が喰らって強くなる（攻撃・最大HP +devour、増えた分だけ回復） */
function devour(att: Actor, ev: Ev) {
  if (att.kind !== 'hero' || !att.spec?.devour || !att.alive) return;
  att.devoured = (att.devoured ?? 0) + 1;
  const oldMax = att.hpMax;
  applyHeroStats(att);
  const grow = Math.max(0, att.hpMax - oldMax);
  if (grow > 0) heal(att, grow, ev, 'devour');
}
/** 武将の攻撃・最大HPを基礎能力 × 妖力の段階 × 喰らった数から計算し直す */
function applyHeroStats(h: Actor) {
  if (!h.spec) return;
  const eff = surgeEffect(h.surge ?? 1);
  const g = devourMult(h);
  h.atk = Math.max(0, Math.round(h.spec.atk * (1 + eff.atk) * g));
  h.hpMax = Math.max(1, Math.round(h.spec.hpMax * (1 + eff.hp) * g));
}

/** お供・屍喰らい（comp:feast）：相手（ヒーロー・妖）を倒したヒーローが最大HPの割合を回復 */
function feast(att: Actor, ev: Ev) {
  const v = compOf(att, 'feast');
  if (v <= 0 || att.kind !== 'hero' || !att.alive || att.hp >= att.hpMax) return;
  const amt = Math.round(att.hpMax * v);
  ev.push({ t: 'companion', id: att.id, ability: 'feast', amount: Math.min(amt, att.hpMax - att.hp) });
  heal(att, amt, ev, 'companion');
}

// ---------- 式神の奥義（★5。shiki:<条件>:<効果>=<値>、1戦に1回） ----------

/** 相手の手番の途中（HPが半分を下回った・相手を倒した）でも働く効果だけ。周りへの攻撃・本陣への攻撃は自分の手番の条件だけ */
const OUGI_SAFE_TRIGGERS = new Set(['pinch', 'kill']);

/** 奥義の条件 trigger が満たされたら効果を出す（1戦に1回）。出たら true。companion イベント（ability: 'ougi'）のあとに効果のイベントが続く */
export function tryOugi(b: Battle, a: Actor | undefined, trigger: string, ev: Ev): boolean {
  const o = a?.spec?.ougi;
  if (!a || !o || a.ougiUsed || o.trigger !== trigger || !a.alive || a.cell == null || over(b) || !isUnitSide(a.side)) return false;
  a.ougiCount = (a.ougiCount ?? 0) + 1;
  if (a.ougiCount >= (o.uses ?? 1)) a.ougiUsed = true;
  a.ougiRevive = false;
  const side = a.side;
  const e = o.effects;
  const n = (k: string) => Math.floor((e[k] ?? 0) + 1e-9);
  ev.push({ t: 'companion', id: a.id, ability: 'ougi', amount: 0 });
  const foe = heroOf(b, enemyOf(side));
  // 周りの相手へ（自分から ougiBlastRange マス以内のヒーロー・妖。攻撃の倍率で、防御は効く）
  if (e.blast && !OUGI_SAFE_TRIGGERS.has(trigger)) {
    const at = a.cell;
    for (const u of b.actors) {
      if (over(b)) break;
      if (!u.alive || u.cell == null || u.side === side || u.kind === 'building' || dist(u.cell, at) > CFG.flags.ougiBlastRange) continue;
      ev.push({ t: 'splash', id: a.id, cell: u.cell });
      hitActor(b, u, damageCalc(b, a, u, false, e.blast), a, ev);
    }
  }
  // 敵本陣へ（本陣の最大HPの割合。結界中は結界の倍率）
  if (e.quake && !OUGI_SAFE_TRIGGERS.has(trigger) && !over(b) && !b.defense) {
    const es = enemyOf(side);
    const core = b.cores[es];
    if (core) hitCore(b, es, Math.max(1, Math.round(core.max * e.quake * (wardOn(b, es) ? CFG.ward : 1))), a, ev);
  }
  if (over(b)) return true;
  if (e.heal) heal(a, Math.round(a.hpMax * e.heal), ev, 'companion');
  if (n('guard') > 0) a.ougiGuard = (a.ougiGuard ?? 0) + n('guard');
  if (e.power) a.ougiPower = Math.max(a.ougiPower ?? 1, e.power);
  if (e.skill) a.skillReady = Math.min(a.skillReady ?? 0, a.turns ?? 0);
  if (n('mat') > 0) gainMat(b, side, n('mat'), ev, 'companion');
  if (n('paint') > 0) {
    const at = a.cell;
    const cells: number[] = [];
    for (let c = 0; c < b.tiles.length; c++) if (dist(c, at) <= n('paint') && paintable(b, c) && b.tiles[c].owner !== side) cells.push(c);
    for (const c of cells) b.tiles[c].owner = side;
    if (cells.length) ev.push({ t: 'paint', id: a.id, side, cells });
  }
  if (foe && foe.alive && foe.cell != null) {
    if (n('bind') > 0) afflict(b, foe, 'bind', n('bind'), ev, { from: a.id });
    if (n('seal') > 0) afflict(b, foe, 'seal', n('seal'), ev, { from: a.id });
    if (e.burn) afflict(b, foe, 'burn', CFG.flags.ougiStatusTurns, ev, { dmg: Math.max(1, Math.round(foe.hpMax * e.burn)), from: a.id });
  }
  if (n('surge') > 0 && CFG.surge.enabled) gainSurge(b, side, n('surge'), ev);
  // 加勢の追加の効果：剛力・守り（手番の数。手番の終わりに1減るので +1）・移動・建物を建てる（陣材なし）・建物を直す
  if (n('might') > 0) buff(a, 'might', n('might') + 1, ev);
  if (n('ward') > 0) buff(a, 'ward', n('ward') + 1, ev);
  if (n('move') > 0) a.ougiMove = (a.ougiMove ?? 0) + n('move');
  if (n('build') > 0 && !over(b)) raiseFree(b, a, n('build'), ev);
  if (e.repair && !over(b)) for (const u of buildingsOf(b, side)) if (u.hp < u.hpMax) heal(u, Math.round(u.hpMax * e.repair), ev, 'companion');
  return true;
}

/** 加勢：自分の周り2マスに陣材なしで建物を count 個（櫓が3分の2、残りは竹柵） */
function raiseFree(b: Battle, a: Actor, count: number, ev: Ev) {
  const side = a.side as Side;
  for (const r of raiseCells(b, a, { radius: 2, count, costMult: 0 } as JutsuPower)) {
    const st = buildingStats(a, r.type, b.cores[side].max);
    const bld: Actor = {
      id: b.nextId++, kind: 'building', side, enemyId: null, cell: r.cell, hp: st.hp, hpMax: st.hp, atk: 0, def: st.def, agi: 1,
      alive: true, nextAt: 0, defending: false, type: r.type, power: st.power, range: st.range, level: st.level,
    };
    b.actors.push(bld);
    b.prio[bld.id] = Object.keys(b.prio).length;
    if (side === 'blue') b.stats.buildingsBuilt++;
    else b.stats.rivalBuildingsBuilt++;
    ev.push({ t: 'build', id: a.id, bid: bld.id, type: r.type, cell: r.cell, side });
  }
}

/** 式神の定期の加護：手番の初めに、every 手番ごとに1つ重ねる（max まで）。回復の加護は重ねた分の割合を毎回回復 */
function boonTick(b: Battle, a: Actor, ev: Ev) {
  const bo = a.spec?.boon;
  if (!bo || !a.alive || a.cell == null || !isUnitSide(a.side)) return;
  const t = a.turns ?? 0;
  if (t > 0 && t % bo.every === 0 && (a.boonStacks ?? 0) < bo.max) {
    a.boonStacks = (a.boonStacks ?? 0) + 1;
    ev.push({ t: 'companion', id: a.id, ability: `boon_${bo.key}`, amount: a.boonStacks });
  }
  if (bo.key === 'regen' && a.boonStacks && a.hp < a.hpMax) heal(a, Math.max(1, Math.round(a.hpMax * bo.value * a.boonStacks)), ev, 'companion');
}

// ---------- 妖力（戦闘中の成長） ----------

const SURGE_NONE = { stage: 1, points: 0 };
/** 陣営の妖力（古い保存データには無いので段階1・0点として読む） */
export const surgeOf = (b: Battle, side: Side) => b.surge?.[side] ?? SURGE_NONE;
export const surgeMax = () => CFG.surge.thresholds.length;
/** 段階 stage の効果（段階1からの累計） */
export function surgeEffect(stage: number) {
  const st = CFG.surge.stages;
  return st[Math.max(0, Math.min(st.length, stage) - 1)] ?? { atk: 0, hp: 0, heal: 0, cd: 0, core: 0 };
}
/** 累計ポイントから決まる段階 */
/** 陣営の段階ごとの必要ポイント（surge:fast の分を引いたもの） */
export function surgeThresholds(b: Battle, side: Side): number[] {
  const d = heroOf(b, side)?.spec?.surgeHooks?.pointDelta ?? 0;
  if (!d) return CFG.surge.thresholds;
  const out: number[] = [];
  CFG.surge.thresholds.forEach((t, i) => out.push(i === 0 ? t : Math.max(out[i - 1] + 1, t + d)));
  return out;
}
export function surgeStageFor(points: number, thresholds: number[] = CFG.surge.thresholds): number {
  let stage = 1;
  thresholds.forEach((need, i) => {
    if (points >= need) stage = Math.max(stage, i + 1);
  });
  return stage;
}
/** 術の待ち（妖力の短縮込み、最低1） */
export function skillCooldown(b: Battle, a: Actor): number {
  const cd = a.spec!.skill.cd;
  if (!isUnitSide(a.side) || !CFG.surge.enabled) return cd;
  return Math.max(1, cd + (surgeEffect(surgeOf(b, a.side).stage).cd ?? 0) + (traitMods(a).cd ?? 0));
}

/** 特性のキーの段階 stage で解放される特性（その段階の分だけ） */
export function traitsAt(key: string | undefined, stage: number): SurgeTrait[] {
  if (!key) return [];
  return CFG.surge.traits[key]?.[stage] ?? [];
}
/** その段階までに解放された特性 */
export function traitsUpTo(key: string | undefined, stage: number): SurgeTrait[] {
  const out: SurgeTrait[] = [];
  for (let s = 2; s <= stage; s++) out.push(...traitsAt(key, s));
  return out;
}
const NO_MODS: SurgeTraitMods = {};
/** ヒーローの今の特性の効果（解放済みを足し合わせたもの） */
export function traitMods(a: Actor): SurgeTraitMods {
  if (a.kind !== 'hero' || !CFG.surge.enabled || (a.surge ?? 1) <= 1) return NO_MODS;
  const out: Record<string, number> = {};
  const boost = a.spec?.surgeHooks?.boost ?? {};
  for (let st = 2; st <= (a.surge ?? 1); st++) {
    const m = boost[st] ?? 1;
    for (const t of traitsAt(a.spec?.trait, st)) for (const [k, v] of Object.entries(t.mods)) out[k] = (out[k] ?? 0) + (v as number) * m;
  }
  // 移動・一撃離脱・建築の回数は整数
  for (const k of ['move', 'dash', 'buildMax', 'buildPerTurn', 'skillRange', 'cd']) if (out[k] != null) out[k] = Math.round(out[k]);
  return out as SurgeTraitMods;
}
/** 術の射程・範囲の割合・移動力・建築の上限（特性込み） */
export const skillRange = (a: Actor) => a.spec!.skill.range + (traitMods(a).skillRange ?? 0) + (a.ougiCasting ? CFG.surge.ougi.range : 0);
export const skillSplash = (a: Actor) => Math.max(a.spec!.skill.splash * (1 + (traitMods(a).splash ?? 0)), a.ougiCasting ? (ougiOf(a)?.splash ?? 0) : 0);
/** 術の遅らせる量（特性・奥義込み） */
const skillDelay = (a: Actor) => a.spec!.skill.delay * (1 + (traitMods(a).delay ?? 0) + (a.ougiCasting ? (ougiOf(a)?.delay ?? 0) : 0));

// ---------- 妖力の奥義（味方だけ。loadout のフラグ surge:ougi） ----------

/** キャラの奥義（名前・上乗せ）。奥義を覚えていない・敵ヒーローなら undefined */
export function ougiOf(a: Actor | undefined) {
  if (!a?.spec || !hasFlag(a, 'surge:ougi')) return undefined;
  return CFG.surge.ougi.chars[a.spec.key];
}
/** 奥義を放てない理由（放てるなら null）：覚えていない・妖力が最大でない・封印中 */
export function ougiBlock(b: Battle, a: Actor | undefined): string | null {
  if (!a || a.kind !== 'hero' || !isUnitSide(a.side) || !CFG.surge.enabled || !ougiOf(a)) return 'locked';
  if (surgeOf(b, a.side).stage < surgeMax()) return 'surge';
  if (hasStatus(a, 'seal')) return 'seal';
  return null;
}
/** 奥義を構えた状態で fn を実行する（射程・範囲・ダメージが奥義の値になる。終われば元に戻す） */
export function castingOugi<T>(a: Actor, fn: () => T): T {
  const prev = a.ougiCasting;
  a.ougiCasting = true;
  try {
    return fn();
  } finally {
    if (prev) a.ougiCasting = prev;
    else delete a.ougiCasting;
  }
}
/** 奥義の後：妖力を段階1・0点に戻し、能力も段階1に戻す */
export function dropSurge(b: Battle, side: Side, ev: Ev, stage = 1) {
  if (!b.surge) return;
  const s = b.surge[side];
  const prev = s.stage;
  s.stage = Math.max(1, Math.min(prev, stage));
  s.points = s.stage > 1 ? surgeThresholds(b, side)[s.stage - 1] ?? 0 : 0;
  const h = heroOf(b, side);
  if (h) h.surge = s.stage;
  ev.push({ t: 'surge', id: h?.id ?? 0, side, stage: s.stage, prev, points: s.points, traits: [], effects: [], drop: true });
  if (!h?.spec) return;
  applyHeroStats(h);
  if (h.hp > h.hpMax) h.hp = h.hpMax;
}
export const heroMove = (a: Actor) => (a.spec ? CFG.moveBase : 0) + (a.spec?.move ?? 0) + (traitMods(a).move ?? 0);
export const buildPerTurn = (a: Actor) => a.spec!.build.perTurn + (traitMods(a).buildPerTurn ?? 0);
export const buildMaxCount = (a: Actor) => a.spec!.build.maxCount + (traitMods(a).buildMax ?? 0);

/** 撃破ポイントを足す（stages>0 なら段階を即上げる：ガーディアン）。段階が上がればヒーローを強化する */
export function gainSurge(b: Battle, side: Side, points: number, ev: Ev, stages = 0) {
  if (!CFG.surge.enabled || (points <= 0 && stages <= 0)) return;
  if (!b.surge) b.surge = { blue: { ...SURGE_NONE }, red: { ...SURGE_NONE } };
  const s = b.surge[side];
  const max = surgeMax();
  const prev = s.stage;
  const h0 = heroOf(b, side);
  const cdBefore = h0?.spec ? skillCooldown(b, h0) : 0;
  s.points += Math.max(0, points);
  if (stages > 0) {
    const target = Math.min(max, prev + stages);
    s.points = Math.max(s.points, surgeThresholds(b, side)[target - 1]);
  }
  s.stage = Math.min(max, Math.max(prev, surgeStageFor(s.points, surgeThresholds(b, side))));
  if (s.stage === prev) return;
  const h = heroOf(b, side);
  const unlocked: SurgeTrait[] = [];
  const effects: string[] = [];
  for (let st = prev + 1; st <= s.stage; st++) {
    unlocked.push(...traitsAt(h?.spec?.trait, st));
    effects.push(...(h?.spec?.surgeHooks?.on[st] ?? []));
  }
  if (h) h.surge = s.stage;
  ev.push({ t: 'surge', id: h?.id ?? 0, side, stage: s.stage, prev, points: s.points, traits: unlocked.map((t) => ({ id: t.id, name: t.name, desc: t.desc })), effects });
  if (!h || !h.spec) return;
  const eff = surgeEffect(s.stage);
  const oldMax = h.hpMax;
  applyHeroStats(h);
  if (!h.alive) return; // 倒れている間は最大HPだけ（復活で満タン）
  const grow = Math.max(0, h.hpMax - oldMax);
  const healAmt = grow + Math.round(h.hpMax * (eff.heal ?? 0));
  if (healAmt > 0) heal(h, healAmt, ev, 'surge');
  // 術の待ちが短くなった段階では、いま待っている分も縮める
  const cdDrop = cdBefore - skillCooldown(b, h);
  if (cdDrop > 0 && (h.skillReady ?? 0) > (h.turns ?? 0)) h.skillReady = Math.max(h.turns ?? 0, (h.skillReady ?? 0) - cdDrop);
  // 段階到達の追加効果（surge:on<段>:<効果>）
  for (const e of effects) {
    if (e === 'heal') heal(h, h.hpMax, ev, 'surge');
    else if (e === 'mat') gainMat(b, side, 5, ev, 'surge');
    else if (e === 'skill') h.skillReady = Math.min(h.skillReady ?? 0, h.turns ?? 0);
  }
  // 式神の奥義：妖力が最大の段階に上がった（倒れている間なら次の手番の最初）
  if (s.stage >= max && prev < max) tryOugi(b, h, 'surge3', ev);
}

// ---------- 中立・ガーディアンの手番 ----------

export function neutralTurn(b: Battle, ev: Ev) {
  const a = currentActor(b)!;
  if (a.attacker) attackerTurn(b, a, ev);
  else if (a.siegeTarget) siegeTurn(b, a, ev);
  else if (a.kind === 'boss') bossTurn(b, a, ev);
  else monsterTurn(b, a, ev);
  if (!over(b)) {
    a.nextAt = b.time + interval(b, a);
    b.phase = 'idle';
    b.current = null;
  }
  if (a.alive) statusTick(a, ev);
  ev.push({ t: 'end', id: a.id });
}

const leash = (a: Actor) => (a.tier === 'raider' ? CFG.raider : CFG.enemy);

function heroesNear(b: Battle, a: Actor) {
  const E = leash(a);
  return b.actors.filter((h) => h.kind === 'hero' && h.alive && h.cell != null && (dist(h.cell, a.camp as number) <= E.roam || dist(h.cell, a.cell as number) <= E.aggro));
}

function chooseTarget(a: Actor, list: Actor[]): Actor {
  return list
    .slice()
    .sort((x, y) => dist(a.cell!, x.cell!) - dist(a.cell!, y.cell!) || Number(y.side === a.lastHit) - Number(x.side === a.lastHit) || x.id - y.id)[0];
}

function checkAbsence(b: Battle, a: Actor, near: Actor[], ev: Ev): boolean {
  if (near.length) {
    a.absence = 0;
    return false;
  }
  a.absence = (a.absence ?? 0) + 1;
  if (a.absence >= CFG.neutralResetTurns) {
    if (!unitAt(b, a.camp as number)) a.cell = a.camp as number;
    a.hp = a.hpMax;
    a.engaged = false;
    a.absence = 0;
    a.dmg = { blue: 0, red: 0 };
    a.telegraph = null;
    a.pattern = 0;
    if (a.armorMax) a.armor = a.armorMax;
    ev.push({ t: 'reset', id: a.id, cell: a.cell as number, hp: a.hp });
  }
  return true;
}

/** 中立の移動（狐火の判定つき） */
function neutralMove(b: Battle, a: Actor, path: number[], ev: Ev) {
  // 攻め手は相手（自分）の落とし穴で止まる
  if (a.attacker) {
    const at = path.slice(1).findIndex((c) => b.actors.some((u) => u.alive && u.type === 'otoshiana' && u.side !== a.side && u.cell === c));
    if (at >= 0) path = path.slice(0, at + 2);
  }
  const burns = lanternHits(b, a.side, path.slice(1));
  a.cell = path[path.length - 1];
  ev.push({ t: 'move', id: a.id, path, painted: [] });
  for (const { lantern, cell } of burns) {
    if (!a.alive || !lantern.alive || over(b)) break;
    ev.push({ t: 'kitsunebi', id: a.id, cell, bid: lantern.id });
    hitActor(b, a, lantern.power ?? 1, lantern, ev);
  }
  const trap = a.attacker && a.alive ? b.actors.find((u) => u.alive && u.type === 'otoshiana' && u.side !== a.side && u.cell === a.cell) : undefined;
  if (trap && !over(b)) {
    trap.alive = false;
    trap.cell = null;
    ev.push({ t: 'trap', id: a.id, cell: a.cell as number, bid: trap.id });
    hitActor(b, a, trap.power ?? 1, trap, ev);
  }
}

function monsterTurn(b: Battle, a: Actor, ev: Ev) {
  const E = leash(a);
  const near = heroesNear(b, a);
  // 持ち場から動かない（プロローグの一本道を崩さない）。隣のヒーローだけ殴る
  if (a.hold) {
    const adj = near.filter((h) => adjacent(h.cell!, a.cell!));
    if (adj.length) {
      const target = chooseTarget(a, adj);
      const dmg = damageCalc(b, a, target);
      ev.push({ t: 'attack', id: a.id, target: { kind: 'actor', id: target.id }, skill: null, dmg });
      hitActor(b, target, dmg, a, ev);
      counterAttack(b, target, a, ev);
    }
    return;
  }
  if (!a.engaged) {
    if (near.some((h) => dist(h.cell!, a.cell!) <= E.aggro)) a.engaged = true;
    else {
      wander(b, a, ev);
      return;
    }
  }
  if (checkAbsence(b, a, near, ev)) return;
  if (a.telegraph) {
    resolveWarning(b, a, ev, CFG.eliteStrikeMult);
    return;
  }
  const target = chooseTarget(a, near);
  if (a.tier === 'elite' && adjacent(a.cell!, target.cell!) && (a.pattern = (a.pattern ?? 0) + 1) % 2 === 0) {
    warn(b, a, [target.cell!, ...neighbors(target.cell!).filter((c) => adjacent(c, a.cell!))], ev);
    return;
  }
  if (!adjacent(a.cell!, target.cell!)) {
    // キャンプの周りを幅優先で探し、岩を横に回り込む
    const queue: number[][] = [[a.cell!]];
    const seen = new Set([a.cell!]);
    let route: number[] | null = null;
    for (let i = 0; i < queue.length; i++) {
      const path = queue[i];
      const cell = path[path.length - 1];
      if (adjacent(cell, target.cell!)) {
        route = path;
        break;
      }
      for (const n of neighbors(cell))
        if (!seen.has(n) && passable(b, n, a) && dist(n, a.camp!) <= E.roam && b.tiles[n].t !== 'home') {
          seen.add(n);
          queue.push([...path, n]);
        }
    }
    if (route && route.length > 1) neutralMove(b, a, route.slice(0, E.steps + 1), ev);
  }
  if (a.alive && target.alive && adjacent(a.cell!, target.cell!)) {
    const dmg = damageCalc(b, a, target);
    ev.push({ t: 'attack', id: a.id, target: { kind: 'actor', id: target.id }, skill: null, dmg });
    hitActor(b, target, dmg, a, ev);
    counterAttack(b, target, a, ev);
  }
}

/** 試作：本陣が削れるたびに守備の妖が本陣の隣に湧く（CFG.coreGuards） */
function spawnCoreGuards(b: Battle, side: Side, ev: Ev) {
  if (!CFG.coreGuards.length || b.defense) return;
  const core = b.cores[side];
  const done = (b.coreGuardsDone ??= { blue: 0, red: 0 });
  while (done[side] < CFG.coreGuards.length && core.hp < core.max * CFG.coreGuards[done[side]]) {
    done[side]++;
    const tpl = b.actors.find((u) => u.kind === 'neutral' && u.tier === 'medium') ?? b.actors.find((u) => u.kind === 'neutral');
    if (!tpl) return;
    const at = neighbors(core.cell).find((c) => passable(b, c, tpl) && !unitAt(b, c));
    if (at == null) return;
    const k = CFG.coreGuardMult;
    const a: Actor = {
      id: b.nextId++, kind: 'neutral', side: 'neutral', enemyId: tpl.enemyId, cell: at, camp: at,
      hp: Math.round(tpl.hpMax * k), hpMax: Math.round(tpl.hpMax * k), atk: Math.round(tpl.atk * k), def: tpl.def, agi: tpl.agi,
      alive: true, nextAt: b.time + interval(b, tpl), defending: false, tier: 'medium', engaged: true, absence: 0,
      dmg: { blue: 0, red: 0 }, telegraph: null, pattern: 0, lastHit: null,
    };
    b.actors.push(a);
    b.prio[a.id] = Object.keys(b.prio).length;
    ev.push({ t: 'spawn', id: a.id, cell: at });
  }
}

/** 中盤の増援：左右の脇に1体ずつ */
function spawnWaves(b: Battle, ev: Ev) {
  while (b.wavesDone < b.waves.length && b.time >= b.waves[b.wavesDone]) {
    b.wavesDone++;
    const flank = CELLS.filter((c) => c.row === 5 && b.tiles[c.i].t !== 'block' && b.tiles[c.i].t !== 'core' && b.tiles[c.i].t !== 'boss' && !unitAt(b, c.i)).sort(
      (x, y) => Math.abs(y.col - 3) - Math.abs(x.col - 3) || x.i - y.i,
    );
    const left = flank.find((c) => c.col < 3);
    const right = flank.find((c) => c.col > 3);
    const tpl = b.raider;
    if (!tpl) return;
    const siege = ENEMIES[tpl.enemyId]?.type === 'siege';
    for (const c of [left, right]) {
      if (!c) continue;
      const a: Actor = equipEnemyType({
        id: b.nextId++,
        kind: 'neutral',
        side: 'neutral',
        enemyId: tpl.enemyId,
        cell: c.i,
        camp: c.i,
        hp: tpl.hp,
        hpMax: tpl.hp,
        atk: tpl.atk,
        def: tpl.def,
        agi: tpl.agi,
        alive: true,
        nextAt: 0,
        defending: false,
        tier: 'raider',
        engaged: false,
        absence: 0,
        dmg: { blue: 0, red: 0 },
        telegraph: null,
        pattern: 0,
        lastHit: null,
      });
      // 攻城：左の脇から出たら自分（青）の本陣、右なら敵（赤）の本陣へ向かう
      if (siege) a.siegeTarget = c === left ? 'blue' : 'red';
      a.nextAt = b.time + interval(b, a);
      b.actors.push(a);
      b.prio[a.id] = Object.keys(b.prio).length;
      ev.push({ t: 'spawn', id: a.id, cell: c.i, ...(a.siegeTarget ? { siege: a.siegeTarget } : {}) });
    }
  }
}

/** 交戦していない中立は2手番に1回、キャンプの周りをうろつく */
function wander(b: Battle, a: Actor, ev: Ev) {
  a.pattern = (a.pattern ?? 0) + 1;
  if (a.pattern % 2) return;
  const options = neighbors(a.cell!).filter((n) => passable(b, n, a) && dist(n, a.camp!) <= 1 && b.tiles[n].t !== 'home');
  if (!options.length) return;
  neutralMove(b, a, [a.cell!, options[randInt(b, options.length)]], ev);
}

/** ガーディアン：通常攻撃2回のあと大技を予告し、範囲にいたヒーローが1回動いてから撃つ */
function bossTurn(b: Battle, a: Actor, ev: Ev) {
  if (a.telegraph) {
    resolveWarning(b, a, ev, CFG.bossStrikeMult);
    return;
  }
  if (a.roused != null) return rousedTurn(b, a, ev);
  const near = b.actors.filter((h) => h.kind === 'hero' && h.alive && h.side !== a.side && h.cell != null && dist(h.cell, a.cell!) <= CFG.campLeash);
  if (!a.engaged) {
    if (near.some((h) => adjacent(h.cell!, a.cell!))) a.engaged = true;
    else return;
  }
  if (checkAbsence(b, a, near, ev)) return;
  a.pattern = (a.pattern ?? 0) + 1;
  const target = chooseTarget(a, near);
  if (a.pattern % 3 === 0) {
    const d = dirToward(a.cell!, target.cell!);
    let cells: (number | undefined)[] = [d - 1, d, d + 1].map((k) => step(a.cell!, k));
    if (a.bossPattern === 'line') {
      const first = step(a.cell!, d);
      cells = [first, first === undefined ? undefined : step(first, d)];
    }
    if (a.bossPattern === 'burst' && a.hp <= a.hpMax * 0.5) cells = neighbors(a.cell!);
    warn(b, a, cells.filter((c): c is number => c !== undefined && b.tiles[c].t !== 'block'), ev);
    return;
  }
  const adj = near.filter((h) => adjacent(h.cell!, a.cell!));
  if (adj.length) {
    const t = chooseTarget(a, adj);
    const dmg = damageCalc(b, a, t);
    ev.push({ t: 'attack', id: a.id, target: { kind: 'actor', id: t.id }, skill: null, dmg });
    hitActor(b, t, dmg, a, ev);
    counterAttack(b, t, a, ev);
  }
}

/** 目を覚ました番人：持ち場から guardianRouseLeash マスまで踏み出して迎え撃ち、隣なら殴る（3手番に1回は大技の予告）。相手が届かなくなれば持ち場へ戻って落ち着く */
function rousedTurn(b: Battle, a: Actor, ev: Ev) {
  const post = a.camp as number;
  const leash = CFG.guardianRouseLeash;
  const ok = (h: Actor) => h.kind === 'hero' && h.alive && h.side !== a.side && h.cell != null && dist(h.cell, post) <= leash + 1;
  const foes = b.actors.filter(ok);
  const target = foes.find((h) => h.id === a.roused) ?? (foes.length ? chooseTarget(a, foes) : undefined);
  // 道：持ち場から leash マスの中だけ（持ち場のマスは番人だけが戻れる）
  const route = (goal: (c: number) => boolean) => {
    const queue: number[][] = [[a.cell!]];
    const seen = new Set([a.cell!]);
    for (let i = 0; i < queue.length; i++) {
      const path = queue[i];
      const cell = path[path.length - 1];
      if (goal(cell)) return path;
      for (const n of neighbors(cell))
        if (!seen.has(n) && dist(n, post) <= leash && (n === post || (passable(b, n, a) && b.tiles[n].t !== 'home')) && !unitAt(b, n)) {
          seen.add(n);
          queue.push([...path, n]);
        }
    }
    return null;
  };
  if (!target) {
    // 相手が離れた：持ち場へ戻り、着いたら落ち着く
    if (a.cell !== post) {
      const r = route((c) => c === post);
      if (r && r.length > 1) neutralMove(b, a, r.slice(0, CFG.guardianRouse + 1), ev);
    }
    if (a.cell === post) a.roused = undefined;
    return;
  }
  a.roused = target.id;
  if (!adjacent(a.cell!, target.cell!)) {
    const r = route((c) => adjacent(c, target.cell!));
    if (r && r.length > 1) neutralMove(b, a, r.slice(0, CFG.guardianRouse + 1), ev);
    if (!a.alive || !adjacent(a.cell!, target.cell!)) return;
  }
  a.pattern = (a.pattern ?? 0) + 1;
  if (a.pattern % 3 === 0) {
    warn(b, a, [target.cell!, ...neighbors(target.cell!).filter((c) => adjacent(c, a.cell!))], ev);
    return;
  }
  const dmg = damageCalc(b, a, target, false, CFG.guardianRouseAtk);
  ev.push({ t: 'attack', id: a.id, target: { kind: 'actor', id: target.id }, skill: null, dmg });
  hitActor(b, target, dmg, a, ev);
  counterAttack(b, target, a, ev);
}

function warn(b: Battle, a: Actor, cells: number[], ev: Ev) {
  const wait = b.actors.filter((h) => h.kind === 'hero' && h.alive && h.cell != null && cells.includes(h.cell)).map((h) => ({ id: h.id, turns: h.turns ?? 0 }));
  a.telegraph = { cells: [...new Set(cells)], wait };
  ev.push({ t: 'telegraph', id: a.id, cells: a.telegraph.cells });
}

function resolveWarning(b: Battle, a: Actor, ev: Ev, mult: number) {
  const tg = a.telegraph!;
  if (
    tg.wait.some((w) => {
      const h = actorById(b, w.id);
      return h?.alive && (h.turns ?? 0) <= w.turns;
    })
  ) {
    ev.push({ t: 'bossWait', id: a.id });
    return;
  }
  const cells = tg.cells;
  a.telegraph = null;
  ev.push({ t: 'strike', id: a.id, cells });
  for (const h of b.actors.filter((u) => u.kind === 'hero' && u.alive && u.side !== a.side && u.cell != null && cells.includes(u.cell))) {
    if (over(b)) break;
    hitActor(b, h, damageCalc(b, a, h, false, mult), a, ev);
  }
}

/**
 * 版3・敵の武将の構え：手番の終わりに術の待ちが明けていて、射程に相手の武将がいれば、その武将を狙って構える（telegraph。cells は相手の今のマス、wait は相手）。
 * 次の手番に、相手が手番を1回得たあと、届けば術を放つ（cmdAttack の strike）。相手は予告を見てから1手番動ける（離れる・守る・回復・先に倒す・壁を建てる）
 */
function rivalChargeAtEnd(b: Battle, a: Actor, ev: Ev) {
  if (a.telegraph || a.cell == null || !rivalCharges(b, a) || !skillReady(a)) return;
  const sk = a.spec!.skill;
  const from = a.cell;
  const target = b.actors
    .filter((u) => u.kind === 'hero' && u.alive && u.side !== a.side && u.cell != null && dist(from, u.cell) <= skillRange(a) && (!sk.line || onLine(from, u.cell)))
    .sort((x, y) => dist(from, x.cell as number) - dist(from, y.cell as number) || x.id - y.id)[0];
  if (!target) return;
  ev.push({ t: 'charge', id: a.id, skill: sk.name });
  warn(b, a, [target.cell as number], ev);
}

/** 版3：この武将の術は相手の武将を狙うとき「構え」が要る（敵の武将だけ。自分を中心に放つ術は対象外） */
export const rivalCharges = (b: Battle, a: Actor) =>
  a.kind === 'hero' && a.side === 'red' && rulesOf(b) >= 3 && CFG.v3.rivalCharge.enabled && !!a.spec && !a.spec.skill.cast;

/** 構えた武将の手番の最初：狙った相手がまだ手番を得ていなければ待つ（手番を使う。番人の大技と同じ）。相手が倒れていれば構えは解ける。戻り値は手番を使ったか */
function rivalStrike(b: Battle, a: Actor, ev: Ev): boolean {
  const tg = a.telegraph;
  if (!tg) return false;
  if (!a.alive || !rivalCharges(b, a) || !tg.wait.some((w) => actorById(b, w.id)?.alive)) {
    a.telegraph = null;
    return false;
  }
  if (
    tg.wait.some((w) => {
      const h = actorById(b, w.id);
      return h?.alive && (h.turns ?? 0) <= w.turns;
    })
  ) {
    ev.push({ t: 'bossWait', id: a.id });
    endTurn(b, a, ev, true);
    return true;
  }
  return false;
}

// ---------- 防衛戦 ----------

/** 第 n 波が来る自分の手番（1始まり） */
export const defenseWaveTurn = (n: number) => CFG.defense.prepTurns + 1 + (n - 1) * CFG.defense.gap;

/** 自分の手番の最初：波の時刻なら攻め手を出す */
function defenseTick(b: Battle, ev: Ev) {
  const D = b.defense!;
  if (D.wave >= D.total || b.stats.playerTurns !== defenseWaveTurn(D.wave + 1)) return;
  D.wave++;
  const defected: number[] = [];
  if (CFG.defense.defect)
    for (const u of b.actors)
      if (u.alive && u.kind === 'neutral' && u.side === 'neutral' && !u.attacker && u.cell != null) {
        u.side = 'red';
        u.attacker = true;
        u.engaged = true;
        u.telegraph = null;
        defected.push(u.id);
      }
  ev.push({ t: 'wave', n: D.wave, total: D.total, defected });
  // 出どころ：敵陣の奥（0〜2行目）。左右の脇から交互に出して、1か所で受け止められないようにする
  const spawnCells = CELLS.filter((c) => c.row <= 2 && b.tiles[c.i].t === 'grass' && !b.tiles[c.i].pickup && !unitAt(b, c.i))
    .sort((x, y) => x.row - y.row || Math.abs(y.col - 3) - Math.abs(x.col - 3) || x.i - y.i);
  const left = spawnCells.filter((c) => c.col < 3).map((c) => c.i);
  const right = spawnCells.filter((c) => c.col >= 3).map((c) => c.i);
  const order: number[] = [];
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    if (left[i] != null) order.push(left[i]);
    if (right[i] != null) order.push(right[i]);
  }
  let k = 0;
  const spawn = (tpl: DefenseUnit, kind: 'neutral' | 'boss', tier: Actor['tier']) => {
    const cell = order.find((c) => !unitAt(b, c));
    if (cell == null) return;
    const a: Actor = equipEnemyType({
      id: b.nextId++,
      kind,
      side: 'red',
      enemyId: tpl.enemyId,
      cell,
      camp: cell,
      hp: tpl.hp,
      hpMax: tpl.hp,
      atk: tpl.atk,
      def: tpl.def,
      agi: tpl.agi,
      alive: true,
      nextAt: 0,
      defending: false,
      tier,
      engaged: true,
      absence: 0,
      dmg: { blue: 0, red: 0 },
      telegraph: null,
      pattern: 0,
      lastHit: null,
      attacker: true,
    });
    a.nextAt = b.time + interval(b, a);
    b.actors.push(a);
    b.prio[a.id] = Object.keys(b.prio).length;
    ev.push({ t: 'spawn', id: a.id, cell });
  };
  for (const kind of CFG.defense.waves[D.wave - 1] ?? []) {
    if (kind === 'leader') {
      if (D.leader) spawn(D.leader, 'boss', 'elite');
      continue;
    }
    const pool = D.pool[kind];
    if (pool.length) spawn(pool[(D.wave + k++) % pool.length], 'neutral', kind);
  }
  checkDefenseWin(b, ev);
}
/** 最後の波まで来て攻め手がいなくなれば勝ち */
export function checkDefenseWin(b: Battle, ev: Ev) {
  const D = b.defense;
  if (!D || over(b) || D.wave < D.total) return;
  if (b.actors.some((u) => u.alive && u.attacker)) return;
  finish(b, 'blue', 'defense', ev);
}

/** 攻め手の経路：自本陣の隣までの最短路。相手（自分）の建物は壊して通る想定で3歩分 */
function attackerRoute(b: Battle, a: Actor, core = b.cores.blue.cell): number[] | null {
  const dist0 = new Map<number, number>([[a.cell!, 0]]);
  const prev = new Map<number, number>();
  const open = [a.cell!];
  let goal: number | null = null;
  while (open.length) {
    let bi = 0;
    for (let k = 1; k < open.length; k++) if (dist0.get(open[k])! < dist0.get(open[bi])!) bi = k;
    const cell = open.splice(bi, 1)[0];
    if (adjacent(cell, core)) {
      goal = cell;
      break;
    }
    for (const n of neighbors(cell)) {
      const t = b.tiles[n];
      if (t.t === 'block' || t.t === 'core' || t.t === 'home') continue;
      const u = unitAt(b, n);
      const wall = u && u.kind === 'building' && u.side !== a.side;
      if (u && !wall && u !== a && u.kind === 'building') continue;
      const cost = dist0.get(cell)! + (wall ? 3 : u && u.kind !== 'building' ? 2 : 1);
      if (cost < (dist0.get(n) ?? Infinity)) {
        dist0.set(n, cost);
        prev.set(n, cell);
        open.push(n);
      }
    }
  }
  if (goal == null) return null;
  const path = [goal];
  while (path[0] !== a.cell) path.unshift(prev.get(path[0])!);
  return path;
}

/** 攻め手の手番：隣のヒーロー → 自本陣 → 道をふさぐ建物の順に殴り、届かなければ本陣へ進む */
function attackerTurn(b: Battle, a: Actor, ev: Ev) {
  const coreCell = b.cores.blue.cell;
  const hitCoreNow = () => {
    if (!adjacent(a.cell!, coreCell)) return false;
    const t: CoreTarget = { kind: 'core', side: 'blue', ...b.cores.blue };
    const dmg = damageCalc(b, a, t);
    ev.push({ t: 'attack', id: a.id, target: { kind: 'core', side: 'blue' }, skill: null, dmg });
    hitCore(b, 'blue', dmg, a, ev);
    return true;
  };
  const hit = (u: Actor) => {
    const dmg = damageCalc(b, a, u);
    ev.push({ t: 'attack', id: a.id, target: { kind: 'actor', id: u.id }, skill: null, dmg });
    hitActor(b, u, dmg, a, ev);
    if (u.kind === 'hero') counterAttack(b, u, a, ev);
  };
  // とにかく本陣：届くなら本陣だけを殴る。ヒーローは道をふさいでいる時か、動けない時だけ相手にする
  if (hitCoreNow()) return;
  const route = attackerRoute(b, a);
  const walk = [a.cell!];
  if (route)
    for (const c of route.slice(1, CFG.defense.steps + 1)) {
      if (!passable(b, c, a)) break;
      walk.push(c);
    }
  if (walk.length > 1) neutralMove(b, a, walk, ev);
  if (!a.alive || over(b)) return;
  if (hitCoreNow()) return;
  const next = route ? route[route.indexOf(a.cell!) + 1] : undefined;
  const blocker = next != null ? unitAt(b, next) : undefined;
  if (blocker && (blocker.side === 'blue') && (blocker.kind === 'building' || blocker.kind === 'hero')) return hit(blocker);
  if (walk.length <= 1) {
    const h = b.actors.find((x) => x.kind === 'hero' && x.alive && x.side === 'blue' && x.cell != null && adjacent(x.cell, a.cell!));
    if (h) hit(h);
  }
}

// ---------- 攻城（増援で出た攻城の妖） ----------

/** 攻城の手番：狙った本陣に届けば削る。届かなければ進み、道をふさぐ建物・ヒーローだけを殴る */
function siegeTurn(b: Battle, a: Actor, ev: Ev) {
  const T = CFG.enemyTypes.siege;
  const side = a.siegeTarget!;
  const core = b.cores[side];
  const hitCoreNow = () => {
    if (!adjacent(a.cell!, core.cell)) return false;
    const dmg = Math.max(1, Math.round(core.max * T.corePct * (wardOn(b, side) ? CFG.ward : 1)));
    ev.push({ t: 'attack', id: a.id, target: { kind: 'core', side }, skill: null, dmg });
    hitCore(b, side, dmg, a, ev);
    return true;
  };
  if (hitCoreNow()) return;
  const route = attackerRoute(b, a, core.cell);
  const walk = [a.cell!];
  if (route)
    for (const c of route.slice(1, T.steps + 1)) {
      if (!passable(b, c, a) || b.tiles[c].t === 'home') break;
      walk.push(c);
    }
  if (walk.length > 1) neutralMove(b, a, walk, ev);
  if (!a.alive || over(b)) return;
  if (hitCoreNow()) return;
  const hit = (u: Actor, mult: number) => {
    const dmg = damageCalc(b, a, u, false, mult);
    ev.push({ t: 'attack', id: a.id, target: { kind: 'actor', id: u.id }, skill: null, dmg });
    hitActor(b, u, dmg, a, ev);
    if (u.kind === 'hero') counterAttack(b, u, a, ev);
  };
  const next = route ? route[route.indexOf(a.cell!) + 1] : undefined;
  const blocker = next != null ? unitAt(b, next) : undefined;
  if (blocker && blocker.kind === 'building') return hit(blocker, T.building);
  if (blocker && blocker.kind === 'hero') return hit(blocker, T.hero);
  if (walk.length <= 1) {
    const h = b.actors.find((x) => x.kind === 'hero' && x.alive && x.cell != null && adjacent(x.cell, a.cell!));
    if (h) hit(h, T.hero);
  }
}

// ---------------- 術（自分を中心に放つ。data/jutsu.ts の strike 以外） ----------------

/** 持っている術（自分を中心に放つもの）。相手を狙う術なら undefined */
export const castOf = (a: Actor | undefined): JutsuDef | undefined => {
  const id = a?.kind === 'hero' ? a.spec?.skill.cast : undefined;
  return id ? JUTSU_BY_ID[id] : undefined;
};
/** 術の数値（奥義なら奥義版） */
export const castPower = (a: Actor, ougi: boolean): JutsuPower | undefined => {
  const j = castOf(a);
  return j ? (ougi ? j.ougi : j.power) : undefined;
};
const around = (cell: number, r: number) => CELLS.filter((c) => dist(c.i, cell) <= r).map((c) => c.i);
export const CELLS_AROUND = around;
/** 跳べる・押し出せる先（盤の上の、通れる空きマス） */
const openCell = (b: Battle, cell: number) => {
  const t = b.tiles[cell];
  return t.t !== 'block' && t.t !== 'core' && !unitAt(b, cell);
};
/** 狙える敵（ヒーロー・中立・番人。建物は狙わない） */
const foeUnit = (u: Actor, side: Side) => u.alive && u.cell != null && u.kind !== 'building' && u.side !== side;

/** 選んだマスに放つ術（送り火）の狙えるマス：自分から range マス以内の、岩でないマス */
export function aimCells(b: Battle, a: Actor, range: number): number[] {
  if (a.cell == null) return [];
  return around(a.cell, range).filter((c) => b.tiles[c].t !== 'block');
}

/** 縮地の着地点：6方向の直線で radius マス以内の空きマス（障害物は越える） */
export function leapCells(b: Battle, a: Actor, radius: number): number[] {
  const out: number[] = [];
  if (a.cell == null) return out;
  for (let d = 0; d < 6; d++) {
    let c: number | undefined = a.cell;
    for (let k = 1; k <= radius; k++) {
      c = step(c, d);
      if (c == null) break;
      if (openCell(b, c)) out.push(c);
    }
  }
  return out;
}
/** 縮地で斬る相手：着地点の隣の敵（ヒーロー → 番人 → HPの少ない順）。奥義なら隣の敵本陣も */
export function leapTarget(b: Battle, a: Actor, cell: number, ougi: boolean): TargetRef | null {
  const side = a.side as Side;
  const foes = b.actors.filter((u) => foeUnit(u, side) && adjacent(u.cell as number, cell));
  foes.sort((x, y) => (x.kind === 'hero' ? 0 : x.kind === 'boss' ? 1 : 2) - (y.kind === 'hero' ? 0 : y.kind === 'boss' ? 1 : 2) || x.hp - y.hp || x.id - y.id);
  const foeCore = b.cores[enemyOf(side)];
  if (ougi && castPower(a, true)?.core && adjacent(foeCore.cell, cell)) return { kind: 'core', side: enemyOf(side) };
  return foes[0] ? { kind: 'actor', id: foes[0].id } : null;
}

/** 術を放てない理由（放てるなら null） */
export function castBlock(b: Battle, a: Actor | undefined, ougi: boolean, cell?: number): string | null {
  if (!a || a.kind !== 'hero' || !canAct(b) || a.dashing) return 'phase';
  const j = castOf(a);
  const p = j && castPower(a, ougi);
  if (!j || !p) return 'locked';
  if (ougi) {
    const why = ougiBlock(b, a);
    if (why) return why;
  } else if (!skillReady(a)) return 'cooldown';
  if (j.kind === 'leap' && (cell == null || !leapCells(b, a, p.radius).includes(cell))) return 'range';
  if (p.range && (cell == null || !aimCells(b, a, p.range).includes(cell))) return 'range';
  if (j.kind === 'raise' && !raiseCells(b, a, p).length) return 'cells';
  return null;
}

/** 突貫普請で建てるマスと種類（櫓は敵本陣に近いマスから、残りは竹柵）。陣材が足りる分だけ */
export function raiseCells(b: Battle, a: Actor, p: JutsuPower): { cell: number; type: BuildingType }[] {
  const side = a.side as Side;
  const foeCore = b.cores[enemyOf(side)].cell;
  const cells = around(a.cell as number, p.radius)
    .filter((c) => c !== a.cell && b.tiles[c].t === 'grass' && !b.tiles[c].relay && !b.tiles[c].pickup && !unitAt(b, c))
    .filter((c) => dist(c, b.cores.blue.cell) > 1 && dist(c, b.cores.red.cell) > 1)
    .sort((x, y) => dist(x, foeCore) - dist(y, foeCore) || x - y);
  const count = Math.min(p.count ?? 3, cells.length);
  // 櫓が3分の2、残りは竹柵
  const towers = Math.max(1, Math.round((count * 2) / 3));
  let mat = b.mat[side];
  const out: { cell: number; type: BuildingType }[] = [];
  for (let i = 0; i < count; i++) {
    const type: BuildingType = i < towers ? 'yagura' : 'sakaku';
    const cost = Math.max(0, Math.round(CFG.buildings[type].cost * (p.costMult ?? 1)));
    if (mat < cost) continue;
    mat -= cost;
    out.push({ cell: cells[i], type });
  }
  return out;
}

/** マスを塗る（陣地の段階が変われば知らせる。龍脈は取る） */
function paintCells(b: Battle, a: Actor, cells: number[], ev: Ev): number[] {
  const side = a.side as Side;
  const before = { blue: territoryTier(b, 'blue'), red: territoryTier(b, 'red') };
  const painted: number[] = [];
  for (const c of cells) {
    const t = b.tiles[c];
    if (!paintable(b, c) || t.owner === side) continue;
    t.owner = side;
    painted.push(c);
    if (t.relay) {
      ev.push({ t: 'capture', id: a.id, cell: c, side });
      relayHeal(a, ev);
    }
  }
  if (side === 'blue') b.stats.tiles += painted.length;
  if (painted.length) ev.push({ t: 'paint', id: a.id, side, cells: painted });
  for (const s of SIDES) {
    const tier = territoryTier(b, s);
    if (tier !== before[s]) ev.push({ t: 'territory', side: s, tier, prev: before[s] });
  }
  return painted;
}

/** 術のダメージ（攻撃力 × 術の倍率 × スキルの術の強化。通常攻撃と同じ防御の計算） */
const castDamage = (b: Battle, a: Actor, target: Target, mult: number) => damageCalc(b, a, target, false, mult * (a.spec!.skill.power ?? 1));
function castHit(b: Battle, a: Actor, ref: TargetRef, mult: number, ev: Ev): number {
  const target = resolveTarget(b, ref);
  if (!target) return 0;
  const dmg = castDamage(b, a, target, mult);
  if (target.kind === 'core') {
    hitCore(b, target.side, dmg, a, ev);
    return dmg;
  }
  return hitActor(b, target, dmg, a, ev);
}

/** 術を放つ（自分を中心に。cell＝縮地の着地点）。術はこの手番の行動になる（放つと手番が終わる） */
export function cmdCast(b: Battle, cell: number | undefined, ougi = false): CmdResult {
  const a = heroTurn(b);
  const why = castBlock(b, a, ougi, cell);
  if (why) return fail(why);
  const hero = a!;
  const j = castOf(hero)!;
  const p = castPower(hero, ougi)!;
  const ev: Ev = [];
  const at = hero.cell as number;
  // 待ち（奥義でも術の待ちは始まる。お供の待ち-1も使う）
  const cool = skillCooldown(b, hero);
  const cdCut = cool > 1 && compOf(hero, 'cd') > 0 && useComp(hero, 'cd') ? 1 : 0;
  hero.skillReady = (hero.turns ?? 0) + cool - cdCut;
  const area = j.kind === 'leap' ? [cell as number] : around(p.range ? (cell as number) : at, p.radius);
  ev.push({ t: 'cast', id: hero.id, jutsu: j.id, name: ougi ? (hero.spec!.ougiName ?? ougiOf(hero)?.name ?? j.name) : j.name, cells: area, ...(ougi ? { ougi: true } : {}) });
  if (cdCut) ev.push({ t: 'companion', id: hero.id, ability: 'cd', amount: cdCut });
  // 術の賽：相手に当てる術（送り火・縮地・旋風・狐火の陣）だけ
  if (['paint', 'leap', 'gust', 'foxfire'].includes(j.kind)) skillDice(b, hero, ev);
  try {
    return castWith(b, hero, j, p, area, at, cell, ougi, ev);
  } finally {
    hero.skillDice = undefined;
  }
}

function castWith(b: Battle, hero: Actor, j: JutsuDef, p: JutsuPower, area: number[], at: number, cell: number | undefined, ougi: boolean, ev: Ev): CmdResult {
  const side = hero.side as Side;
  let dealt = 0;
  switch (j.kind) {
    case 'paint': {
      // 送り火：範囲を自陣にし、範囲の敵に火
      paintCells(b, hero, area, ev);
      for (const u of b.actors.filter((x) => foeUnit(x, side) && area.includes(x.cell as number)))
        if (!over(b) && u.alive) dealt += castHit(b, hero, { kind: 'actor', id: u.id }, p.mult, ev);
      break;
    }
    case 'lantern':
    case 'foxfire': {
      const cells = j.kind === 'lantern' ? area.filter((c) => b.tiles[c].t !== 'block') : area.filter((c) => b.tiles[c].t !== 'block' && b.tiles[c].t !== 'core');
      b.zones = (b.zones ?? []).filter((z) => !(z.owner === hero.id && z.kind === j.kind));
      const burn = Math.max(1, Math.round(hero.atk * (p.burn ?? 0) * (hero.spec!.skill.power ?? 1) * (hero.skillDice ?? 1)));
      b.zones.push({ kind: j.kind, side, owner: hero.id, cells, turns: p.turns ?? 3, ...(j.kind === 'lantern' ? { taken: p.taken, dealt: p.dealt, heal: p.heal, follow: true, radius: p.radius } : { burn }) });
      ev.push({ t: 'zone', side, kind: j.kind, cells, turns: p.turns ?? 3 });
      if (j.kind === 'foxfire')
        for (const u of b.actors.filter((x) => foeUnit(x, side) && cells.includes(x.cell as number))) {
          afflict(b, u, 'burn', 1, ev, { dmg: burn, from: hero.id });
          if (p.delay) delayActor(b, u, p.delay, ev);
        }
      break;
    }
    case 'leap': {
      const to = cell as number;
      hero.cell = to;
      ev.push({ t: 'leap', id: hero.id, from: at, to });
      paintCells(b, hero, [to], ev);
      const ref = leapTarget(b, hero, to, ougi);
      if (ref) dealt += castHit(b, hero, ref, p.mult, ev);
      break;
    }
    case 'gust': {
      const foes = b.actors.filter((x) => foeUnit(x, side) && x.kind !== 'boss' && dist(x.cell as number, at) <= p.radius && x.cell !== at);
      foes.sort((x, y) => dist(y.cell as number, at) - dist(x.cell as number, at));
      for (const u of foes) {
        if (over(b) || !u.alive) continue;
        dealt += castHit(b, hero, { kind: 'actor', id: u.id }, p.mult, ev);
        if (!u.alive || u.cell == null) continue;
        const from = u.cell;
        const d = dirToward(at, from);
        let to = from;
        for (let k = 0; k < (p.push ?? 2); k++) {
          const nx = step(to, d);
          if (nx == null || !openCell(b, nx)) break;
          to = nx;
        }
        if (to !== from) {
          u.cell = to;
          ev.push({ t: 'push', id: u.id, from, to });
        }
        if (p.delay) delayActor(b, u, p.delay, ev);
      }
      break;
    }
    case 'raise': {
      for (const r of raiseCells(b, hero, p)) {
        const cost = Math.max(0, Math.round(CFG.buildings[r.type].cost * (p.costMult ?? 1)));
        b.mat[side] -= cost;
        const st = buildingStats(hero, r.type, b.cores[side].max);
        const bld: Actor = {
          id: b.nextId++, kind: 'building', side, enemyId: null, cell: r.cell, hp: st.hp, hpMax: st.hp, atk: 0, def: st.def, agi: 1,
          alive: true, nextAt: 0, defending: false, type: r.type, power: st.power * (r.type === 'yagura' ? (p.towerMult ?? 1) : 1), range: st.range, level: st.level,
        };
        b.actors.push(bld);
        b.prio[bld.id] = Object.keys(b.prio).length;
        if (side === 'blue') b.stats.buildingsBuilt++;
        else b.stats.rivalBuildingsBuilt++;
        ev.push({ t: 'build', id: hero.id, bid: bld.id, type: r.type, cell: r.cell, side });
      }
      break;
    }
    case 'mend': {
      for (const u of buildingsOf(b, side))
        if (dist(u.cell as number, at) <= p.radius && u.hp < u.hpMax) heal(u, Math.round(u.hpMax * (p.repair ?? 0)), ev, 'skill');
      const own = b.cores[side];
      if (p.coreRepair && (dist(own.cell, at) <= 1 || p.radius >= 99) && own.hp < own.max) {
        const amount = Math.min(own.max - own.hp, Math.round(own.max * p.coreRepair));
        own.hp += amount;
        ev.push({ t: 'coreRepair', side, amount, hp: own.hp });
      }
      // 手番の終わりに1減るので、次の自分の手番まで残るよう +1
      buff(hero, 'ward', (p.turns ?? 1) + 1, ev);
      if (p.heal && hero.hp < hero.hpMax) heal(hero, Math.round(hero.hpMax * p.heal), ev, 'skill');
      break;
    }
    case 'stance': {
      hero.stance = { taken: p.taken ?? 0.5, counter: p.counter ?? 1.5 };
      hero.defending = false;
      ev.push({ t: 'defend', id: hero.id });
      break;
    }
    case 'strike':
      return fail('locked');
  }
  if (hero.alive && dealt > 0) {
    const leech = Math.round(dealt * (hero.spec!.lifesteal + (traitMods(hero).lifesteal ?? 0)));
    if (leech > 0) heal(hero, leech, ev, 'leech');
  }
  if (ougi && !over(b)) dropSurge(b, side, ev);
  // 式神の加勢（術を使ったとき）
  if (!over(b) && hero.alive) tryOugi(b, hero, 'skill', ev);
  // 支援の術（送り火・灯籠陣・突貫普請・鬼の修繕・狐火の陣）は手番を終えない。それ以外は術が行動になる
  if (!over(b) && !FREE_JUTSU.includes(j.kind)) endTurn(b, hero, ev);
  return ok(ev);
}

/** マスが陣の中か（ついて回る陣は持ち主の今の位置から radius マス） */
export function inZone(b: Battle, z: Zone, cell: number): boolean {
  if (!z.follow) return z.cells.includes(cell);
  const o = actorById(b, z.owner);
  return !!o?.alive && o.cell != null && dist(o.cell, cell) <= (z.radius ?? 1);
}
/** 陣の今のマス（ついて回る陣は持ち主のまわり） */
export function zoneCells(b: Battle, z: Zone): number[] {
  if (!z.follow) return z.cells;
  const o = actorById(b, z.owner);
  return o?.alive && o.cell != null ? CELLS.filter((c) => dist(c.i, o.cell!) <= (z.radius ?? 1)).map((c) => c.i) : [];
}
/** 灯籠陣の中にいる自分（と自分の建物）の与ダメの倍率（陣の外なら 1） */
export function lanternDealt(b: Battle, u: Actor): number {
  if (!b.zones || u.cell == null) return 1;
  let m = 1;
  for (const z of b.zones) if (z.kind === 'lantern' && z.side === u.side && inZone(b, z, u.cell)) m = Math.max(m, z.dealt ?? 1);
  return m;
}
/** 灯籠陣の中にいる自分（と自分の建物）の被ダメの倍率（陣の外なら 1） */
export function lanternTaken(b: Battle, u: Actor): number {
  if (!b.zones || u.cell == null) return 1;
  let m = 1;
  for (const z of b.zones) if (z.kind === 'lantern' && z.side === u.side && inZone(b, z, u.cell)) m = Math.min(m, z.taken ?? 1);
  return m;
}
/**
 * 版3・守りの普請（鬼の棟梁 fushin_lead）：自分の本陣の被ダメの倍率（1＝守りなし）。本陣から range マス以内の自分の建物1つごとに -per（上限 max）。
 * AI の本陣の危険の見積もり（damageCalc 経由）にも同じ値が乗る
 */
export function coreGuardMult(b: Battle, side: Side): number {
  if (rulesOf(b) < 3) return 1;
  // 武将が倒れている間も建物は本陣を守る（速攻の武将に武将を倒されてから本陣を割られる負けが多い）
  const h = heroOf(b, side);
  if (!h || !hasFlag(h, 'fushin_lead')) return 1;
  const core = b.cores[side];
  const G = CFG.v3.fushinCoreGuard;
  const n = buildingsOf(b, side).filter((u) => u.cell != null && dist(u.cell, core.cell) <= G.range).length;
  return n ? 1 - Math.min(G.max, G.per * n) : 1;
}
/** 手番の最初：狐火の陣の中の敵は燃える。ヒーローなら自分の陣の残りを減らし、灯籠陣の中なら回復 */
function zoneTurnStart(b: Battle, a: Actor, ev: Ev) {
  if (!b.zones?.length || a.cell == null) return;
  for (const z of b.zones)
    if (z.kind === 'foxfire' && z.side !== a.side && a.kind !== 'building' && z.cells.includes(a.cell)) afflict(b, a, 'burn', 1, ev, { dmg: z.burn ?? 1, from: z.owner });
  if (a.kind !== 'hero') return;
  for (const z of b.zones)
    if (z.kind === 'lantern' && z.side === a.side && inZone(b, z, a.cell) && z.heal && a.hp < a.hpMax) heal(a, Math.round(a.hpMax * z.heal), ev, 'lantern');
  const mine = b.zones.filter((z) => z.owner === a.id);
  for (const z of mine) {
    z.turns--;
    if (z.turns <= 0) ev.push({ t: 'zone', side: z.side, kind: z.kind, cells: [], turns: 0 });
  }
  b.zones = b.zones.filter((z) => z.turns > 0);
}
